import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ArrowDownUp, Settings, Info, Wallet, Loader2, CheckCircle2, XCircle, Plus, Lock, List, ExternalLink, AlertTriangle, Coins, ChevronUp, Server, ChevronDown, Badge } from 'lucide-react';
import TokenSelector from './TokenSelector';
import AddCustomTokenModal from './AddCustomTokenModal';
import CustomTokensManager from './CustomTokensManager';
import AvailableSwapsModal from './AvailableSwapsModal';
import SettingsModal from './SettingsModal';
import AddUTXOAddressesModal from './AddUTXOAddressesModal';
import DepositAddressModal from './DepositAddressModal';
import { toast } from 'sonner';
import { useGetSupportedTokens, useExecuteSwap, useGetCustomTokenPairs, useGetFeeConfig, useCalculateSwapFees, useGetAvailableSwapOffers } from '../hooks/useQueries';
import { useWallet } from '../contexts/WalletContext';
// MM2 Runtime Hook - PAUSED (can be re-enabled in future)
// import { useMM2Runtime } from '../hooks/useMM2Runtime';
import { cn } from '@/lib/utils';

// Test helpers - expose functions for browser automation testing
if (typeof window !== 'undefined') {
    (window as any).__swapInterfaceTestHelpers = {
        // This will be populated after component mounts
    };
}

// Helper to safely extract error messages - prevents binary data from being displayed
function getSafeErrorMessage(error: any, fallback: string = 'An error occurred'): string {
  if (!error) return fallback;
  
  let errorMessage = '';
  
  // Extract message
  if (error.message && typeof error.message === 'string') {
    if (error.message.length > 200 || /^[\d,\s\[\]"]+$/.test(error.message)) {
      errorMessage = fallback;
    } else {
      errorMessage = error.message;
    }
  } else if (typeof error === 'string') {
    if (error.length > 200 || /^[\d,\s\[\]"]+$/.test(error)) {
      errorMessage = fallback;
    } else {
      errorMessage = error;
    }
  } else {
    errorMessage = fallback;
  }
  
  // Map common errors to friendly messages
  if (errorMessage.toLowerCase().includes('insufficient')) {
    return 'Insufficient balance to complete this swap';
  }
  if (errorMessage.toLowerCase().includes('allowance') || errorMessage.toLowerCase().includes('approval')) {
    return 'Token approval required - please approve spending first';
  }
  if (errorMessage.toLowerCase().includes('minimum')) {
    return 'Swap amount below minimum - increase amount and try again';
  }
  if (errorMessage.toLowerCase().includes('slippage')) {
    return 'Price moved too much - try increasing slippage tolerance';
  }
  if (errorMessage.toLowerCase().includes('timeout') || errorMessage.toLowerCase().includes('timed out')) {
    return 'Request timed out - please try again';
  }
  if (errorMessage.toLowerCase().includes('rejected')) {
    return 'Transaction rejected by user';
  }
  if (errorMessage.toLowerCase().includes('kyc')) {
    return 'KYC verification required for this swap amount';
  }
  if (errorMessage.toLowerCase().includes('rate limit')) {
    return 'Rate limit exceeded - please wait a moment and try again';
  }
  if (errorMessage.toLowerCase().includes('liquidity')) {
    return 'Insufficient liquidity for this swap - try a smaller amount';
  }
  
  return errorMessage;
}
import { isAtomicSwapCompatible } from '../lib/atomicSwapUtils';
import { SwapOffer } from '../backend';
import { getOptimalSwapRoute, executeSwapRoute, type RouterQuote } from '../lib/swapRouter';
import { 
  signUTXOTransaction, 
  checkUTXOSupport,
  connectUTXOProvider,
  logWalletCapabilities
} from '../lib/utxoWalletSigner';
import { type ChainId } from '../lib/chainRegistry';
// MM2 Client - PAUSED (can be re-enabled in future)
// import { getMM2Orderbook, isMM2RouteAvailable } from '../lib/mm2Client';
import { requiresDestinationAddress, getDestinationAddressFormat, getDestinationAddressRequirementReason } from '../lib/destinationAddressUtils';
import { validateAddressForChain } from '../lib/addressValidation';
import { getTokenPrimaryChain } from '../lib/tokenRegistry';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { preloadCommonPrices, getCachedPrices, formatPrice } from '../lib/priceService';

interface Token {
    symbol: string;
    name: string;
    chainId?: number; // Optional chain ID for multi-chain tokens
}

type TransactionStatus = 'idle' | 'confirming' | 'processing' | 'success' | 'error';

/**
 * Determines if a swap provider requires wallet connection for signing transactions
 * @param provider - The swap provider name
 * @returns true if wallet signing is required, false if deposit address is sufficient
 */
function requiresWalletSigning(provider?: string): boolean {
    if (!provider) return false;
    
    // These providers need wallet to sign transactions
    // Only active providers that require signing:
    const signingProviders = ['1inch Aggregator', 'ICP Chain Fusion', 'Across Protocol'];
    // Removed: '0x Protocol', 'Wormhole' - both disabled until fee collection configured
    
    // These aggregators only need deposit address (no wallet needed)
    // SimpleSwap, ChangeNOW return deposit addresses that users send funds to manually
    
    return signingProviders.includes(provider);
}

export default function SwapInterface() {
    const { data: backendTokens } = useGetSupportedTokens();
    const { data: userTokenPairsListData } = useGetCustomTokenPairs();
    // CRITICAL: Renamed to userTokenPairsList to avoid bundler hoisting conflicts
    const userTokenPairsList: CustomTokenPair[] = Array.isArray(userTokenPairsListData) ? userTokenPairsListData : [];
    const { data: feeConfig } = useGetFeeConfig();
    const { data: availableOffers } = useGetAvailableSwapOffers();
    const { mutate: executeSwap, isPending: isSwapping } = useExecuteSwap();
    // MM2 Runtime - PAUSED (can be re-enabled in future)
    // const mm2Runtime = useMM2Runtime();
    const { isConnected, balances, walletType, walletAddress, networkInfo } = useWallet();
    const [tokens, setTokens] = useState<Token[]>([]);
    
    // localStorage keys for state persistence (survives remounts)
    const STORAGE_KEYS = {
        fromToken: 'kawswap-fromToken',
        toToken: 'kawswap-toToken',
        fromTokenChainId: 'kawswap-fromTokenChainId',
        toTokenChainId: 'kawswap-toTokenChainId',
        fromAmount: 'kawswap-fromAmount',
        toAmount: 'kawswap-toAmount',
        destinationAddress: 'kawswap-destinationAddress',
        depositInfo: 'kawswap-depositInfo', // Store deposit info including exchangeId
    };
    
    // Load persisted state from localStorage on mount
    const loadPersistedState = (): {
        fromTokenSymbol: string | null;
        toTokenSymbol: string | null;
        fromAmount: string;
        toAmount: string;
        destinationAddress: string;
        depositInfo: {
            depositAddress: string;
            amount: number;
            token: string;
            exchangeId?: string;
            provider: string;
            destinationAddress?: string;
            toToken?: string;
            payinExtraId?: string;
        } | null;
    } => {
        try {
            const depositInfoStr = localStorage.getItem(STORAGE_KEYS.depositInfo);
            let depositInfo = null;
            if (depositInfoStr) {
                try {
                    depositInfo = JSON.parse(depositInfoStr);
                } catch {
                    // Invalid JSON, ignore
                }
            }
            return {
                fromTokenSymbol: localStorage.getItem(STORAGE_KEYS.fromToken),
                toTokenSymbol: localStorage.getItem(STORAGE_KEYS.toToken),
                fromAmount: localStorage.getItem(STORAGE_KEYS.fromAmount) || '',
                toAmount: localStorage.getItem(STORAGE_KEYS.toAmount) || '',
                destinationAddress: localStorage.getItem(STORAGE_KEYS.destinationAddress) || '',
                depositInfo,
            };
        } catch {
            return {
                fromTokenSymbol: null,
                toTokenSymbol: null,
                fromAmount: '',
                toAmount: '',
                destinationAddress: '',
                depositInfo: null,
            };
        }
    };
    
    const persistedState = loadPersistedState();
    
    const [fromToken, setFromToken] = useState<Token | null>(null);
    const [toToken, setToToken] = useState<Token | null>(null);
    const tokensInitialized = useRef(false);
    const [fromAmount, setFromAmount] = useState(persistedState.fromAmount);
    const [toAmount, setToAmount] = useState(persistedState.toAmount);
    const [transactionStatus, setTransactionStatus] = useState<TransactionStatus>('idle');
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [showAddTokenModal, setShowAddTokenModal] = useState(false);
    const [showCustomTokensManager, setShowCustomTokensManager] = useState(false);
    const [showAvailableSwapsModal, setShowAvailableSwapsModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showUTXOAddressesModal, setShowUTXOAddressesModal] = useState(false);
    const [showDepositModal, setShowDepositModal] = useState(false);
    const [depositInfo, setDepositInfo] = useState<{
        depositAddress: string;
        amount: number;
        token: string;
        exchangeId?: string;
        provider: string;
        destinationAddress?: string;
        toToken?: string;
        payinExtraId?: string; // Memo/tag for memo-based coins (XRP, XLM, etc.)
    } | null>(persistedState.depositInfo);
    const [slippageTolerance, setSlippageTolerance] = useState(0.5);
    const [transactionTimeout, setTransactionTimeout] = useState(20);
    const [errorMessage, setErrorMessage] = useState('');
    const [useAtomicSwap, setUseAtomicSwap] = useState(false);
    const [useFallbackRouting, setUseFallbackRouting] = useState(false);
    const [optimalQuote, setOptimalQuote] = useState<RouterQuote | null>(null);
    const [allQuotes, setAllQuotes] = useState<RouterQuote[]>([]);
    const [showAllOptions, setShowAllOptions] = useState(false);
    const [isFetchingRoute, setIsFetchingRoute] = useState(false);
    const [matchingOffer, setMatchingOffer] = useState<SwapOffer | null>(null);
    // MM2 Orderbook Data - PAUSED (can be re-enabled in future)
    // const [mm2OrderbookData, setMm2OrderbookData] = useState<{ bids: number; asks: number; totalVolume: number } | null>(null);
    const [destinationAddress, setDestinationAddress] = useState(persistedState.destinationAddress);
    const [destinationAddressError, setDestinationAddressError] = useState('');
    // Ref to track previous token values to detect actual changes (not just re-renders)
    const prevFromTokenRef = useRef<Token | null>(null);
    const prevToTokenRef = useRef<Token | null>(null);
    const isUserTypingRef = useRef(false);
    const destinationAddressInputRef = useRef<HTMLInputElement | null>(null);
    const userHasEnteredAddressRef = useRef(false); // Track if user has manually entered an address
    const fromAmountInputRef = useRef<HTMLInputElement | null>(null);
    const toAmountInputRef = useRef<HTMLInputElement | null>(null);
    const userIsTypingAmountRef = useRef(false); // Track if user is typing in amount field
    const userHasEnteredAmountRef = useRef(false); // Track if user has manually entered an amount
    const fromAmountTypingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Cleanup timeout for fromAmount typing
    const toAmountTypingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Cleanup timeout for toAmount typing
    const fromAmountBlurTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Cleanup timeout for fromAmount onBlur
    const toAmountBlurTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Cleanup timeout for toAmount onBlur
    const destinationAddressBlurTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Cleanup timeout for destination address onBlur
    const destinationAddressTypingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Cleanup timeout for destination address typing
    const [livePrices, setLivePrices] = useState<Record<string, number>>({});

    // MM2 State - PAUSED (can be re-enabled in future)
    // const mm2RouteReady = mm2Runtime.mode !== 'default';
    // Disable atomic swap toggle - MM2 is paused (native HTLC still works via AtomicSwapInterface)
    const atomicSwapAvailable = false;
    // const mm2StatusLabel = ...
    // const mm2StatusDescription = ...

    // Calculate fees based on fromAmount
    const fromAmountNum = fromAmount && parseFloat(fromAmount) > 0 ? parseFloat(fromAmount) : null;
    
    // Auto-detect chain from wallet or token type (no manual selection needed)
    const walletChainId = networkInfo ? parseInt(networkInfo.chainId, 16) : null;
    const selectedChainId = walletChainId || 1; // Use wallet chain or default to Ethereum
    
    const { data: swapFees } = useCalculateSwapFees(fromAmountNum);
    
    // Persist state to localStorage whenever it changes
    // CRITICAL: Only save when tokens are set, don't remove on null (preserves values during remounts)
    useEffect(() => {
        try {
            if (fromToken?.symbol) {
                localStorage.setItem(STORAGE_KEYS.fromToken, fromToken.symbol);
                if (fromToken.chainId) {
                    localStorage.setItem(STORAGE_KEYS.fromTokenChainId, fromToken.chainId.toString());
                } else {
                    localStorage.removeItem(STORAGE_KEYS.fromTokenChainId);
                }
            }
            // Don't remove on null - allows restoration from localStorage on remount
        } catch (e) {
            // Silently fail if localStorage is not available
        }
    }, [fromToken]);
    
    useEffect(() => {
        try {
            if (toToken?.symbol) {
                localStorage.setItem(STORAGE_KEYS.toToken, toToken.symbol);
                if (toToken.chainId) {
                    localStorage.setItem(STORAGE_KEYS.toTokenChainId, toToken.chainId.toString());
                } else {
                    localStorage.removeItem(STORAGE_KEYS.toTokenChainId);
                }
            }
            // Don't remove on null - allows restoration from localStorage on remount
        } catch (e) {
            // Silently fail if localStorage is not available
        }
    }, [toToken]);
    
    useEffect(() => {
        try {
            if (fromAmount) {
                localStorage.setItem(STORAGE_KEYS.fromAmount, fromAmount);
            } else {
                localStorage.removeItem(STORAGE_KEYS.fromAmount);
            }
        } catch (e) {
            // Silently fail if localStorage is not available
        }
    }, [fromAmount]);
    
    useEffect(() => {
        try {
            if (toAmount) {
                localStorage.setItem(STORAGE_KEYS.toAmount, toAmount);
            } else {
                localStorage.removeItem(STORAGE_KEYS.toAmount);
            }
        } catch (e) {
            // Silently fail if localStorage is not available
        }
    }, [toAmount]);
    
    useEffect(() => {
        try {
            if (destinationAddress) {
                localStorage.setItem(STORAGE_KEYS.destinationAddress, destinationAddress);
            } else {
                localStorage.removeItem(STORAGE_KEYS.destinationAddress);
            }
        } catch (e) {
            // Silently fail if localStorage is not available
        }
    }, [destinationAddress]);
    
    // Persist depositInfo to localStorage (including exchangeId)
    useEffect(() => {
        try {
            if (depositInfo) {
                localStorage.setItem(STORAGE_KEYS.depositInfo, JSON.stringify(depositInfo));
            } else {
                localStorage.removeItem(STORAGE_KEYS.depositInfo);
            }
        } catch (e) {
            // Silently fail if localStorage is not available
        }
    }, [depositInfo]);
    
    // Restore deposit modal on mount if depositInfo exists
    useEffect(() => {
        // Only restore on initial mount if depositInfo exists
        if (persistedState.depositInfo && !showDepositModal) {
            // Small delay to ensure modal is ready
            const timer = setTimeout(() => {
                setShowDepositModal(true);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, []); // Only run on mount

    // Fetch live prices on mount and every 30 seconds (reduced frequency to prevent resource exhaustion)
    useEffect(() => {
        let isMounted = true;
        let intervalId: NodeJS.Timeout | null = null;
        let lastErrorTime = 0;
        const ERROR_BACKOFF_MS = 60000; // Don't retry for 60 seconds after error
        
        const fetchPrices = async () => {
            if (!isMounted) return;
            
            // Don't retry if we recently had an error (prevent resource exhaustion)
            const now = Date.now();
            if (now - lastErrorTime < ERROR_BACKOFF_MS) {
                return;
            }
            
            try {
                // Get custom token symbols for price fetching
                const customSymbols = userTokenPairsList?.map(p => p.symbol) || [];
                await preloadCommonPrices(customSymbols);
                if (!isMounted) return;
                const prices = getCachedPrices();
                
                // Only update if we got prices (prevent setting empty object)
                if (Object.keys(prices).length > 0) {
                    setLivePrices(prev => {
                        // Only update if prices actually changed to prevent re-renders
                        const hasChanged = Object.keys(prices).length !== Object.keys(prev).length ||
                            Object.keys(prices).some(symbol => prices[symbol] !== prev[symbol]);
                        return hasChanged ? prices : prev;
                    });
                    lastErrorTime = 0; // Reset error time on success
                }
            } catch (error) {
                console.error('[SwapInterface] Error fetching prices:', error);
                lastErrorTime = Date.now(); // Record error time to prevent rapid retries
            }
        };

        // Initial fetch
        fetchPrices();

        // Refresh every 30 seconds (reduced from 10 to prevent resource exhaustion)
        intervalId = setInterval(fetchPrices, 30000);

        return () => {
            isMounted = false;
            if (intervalId) clearInterval(intervalId);
        };
    }, []); // Empty dependency array - only run on mount

    useEffect(() => {
        if (backendTokens) {
            const tokenList = backendTokens.map(([symbol, name]) => ({
                symbol,
                name,
            }));
            
            // Add custom token pairs if available
            if (userTokenPairsList && userTokenPairsList.length > 0) {
                const customTokenList = userTokenPairsList.map(pair => ({
                    symbol: pair.symbol,
                    name: pair.name,
                }));
                setTokens([...tokenList, ...customTokenList]);
            } else {
                setTokens(tokenList);
            }
            
            // ONLY set default tokens on first load (don't overwrite user selections or swaps)
            // CRITICAL: Also check that tokens aren't already set to prevent resetting user input
            // IMPORTANT: If tokens are already set (even if tokensInitialized was reset due to remount),
            // do NOT reset them. This prevents token reset bug when component remounts after quotes load.
            const hasTokensSet = fromToken && toToken && fromToken.symbol && toToken.symbol;
            
            // Try to restore tokens from localStorage first (handles remounts)
            // Read directly from localStorage here to get the latest values (persistedState may be stale)
            if (!hasTokensSet && !tokensInitialized.current) {
                try {
                    const storedFromSymbol = localStorage.getItem(STORAGE_KEYS.fromToken);
                    const storedToSymbol = localStorage.getItem(STORAGE_KEYS.toToken);
                    
                    if (storedFromSymbol && storedToSymbol) {
                        const restoredFrom = tokenList.find(t => t.symbol === storedFromSymbol);
                        const restoredTo = tokenList.find(t => t.symbol === storedToSymbol);
                        if (restoredFrom && restoredTo) {
                            // Restore chainId if available
                            const storedFromChainId = localStorage.getItem(STORAGE_KEYS.fromTokenChainId);
                            const storedToChainId = localStorage.getItem(STORAGE_KEYS.toTokenChainId);
                            
                            const fromTokenWithChain: Token = storedFromChainId 
                                ? { ...restoredFrom, chainId: parseInt(storedFromChainId, 10) }
                                : restoredFrom;
                            const toTokenWithChain: Token = storedToChainId
                                ? { ...restoredTo, chainId: parseInt(storedToChainId, 10) }
                                : restoredTo;
                            
                            console.log('[SwapInterface] Restoring tokens from localStorage:', { 
                                from: fromTokenWithChain.symbol, 
                                to: toTokenWithChain.symbol,
                                fromChainId: fromTokenWithChain.chainId,
                                toChainId: toTokenWithChain.chainId
                            });
                            setFromToken(fromTokenWithChain);
                            setToToken(toTokenWithChain);
                            tokensInitialized.current = true;
                            return; // Don't proceed to default initialization
                        } else {
                            console.log('[SwapInterface] Stored tokens not found in token list, using defaults');
                        }
                    }
                } catch (e) {
                    console.warn('[SwapInterface] Error reading from localStorage:', e);
                }
            }
            
            const shouldInitialize = tokenList.length > 0 && !tokensInitialized.current && !hasTokensSet;
            
            if (shouldInitialize) {
                // Only set defaults if localStorage is also empty (first time user visits)
                try {
                    const existingFrom = localStorage.getItem(STORAGE_KEYS.fromToken);
                    const existingTo = localStorage.getItem(STORAGE_KEYS.toToken);
                    // If localStorage has values, don't initialize defaults (restore will happen in next render)
                    if (existingFrom && existingTo) {
                        console.log('[SwapInterface] localStorage has tokens, skipping default initialization');
                        tokensInitialized.current = true;
                        return;
                    }
                } catch (e) {
                    // Continue with defaults if localStorage check fails
                }
                
                const defaultFrom = tokenList.find(t => t.symbol === 'RVN') || tokenList[0];
                const defaultTo = tokenList.find(t => t.symbol === 'BTC') || (tokenList.length > 1 ? tokenList[1] : tokenList[0]);
                console.log('[SwapInterface] Initializing default tokens:', { from: defaultFrom.symbol, to: defaultTo.symbol });
                setFromToken(defaultFrom);
                setToToken(defaultTo);
                // Save defaults to localStorage so they persist across remounts (only if localStorage was empty)
                try {
                    localStorage.setItem(STORAGE_KEYS.fromToken, defaultFrom.symbol);
                    localStorage.setItem(STORAGE_KEYS.toToken, defaultTo.symbol);
                } catch (e) {
                    // Silently fail if localStorage is not available
                }
                tokensInitialized.current = true;
            } else if (hasTokensSet && !tokensInitialized.current) {
                // Tokens are set but ref wasn't initialized (likely remount) - mark as initialized
                tokensInitialized.current = true;
            } else if (tokensInitialized.current || hasTokensSet) {
                // Tokens already initialized OR tokens are set - don't reset them
                // This prevents resetting tokens when backendTokens refetches during quote loading
            }
            // Note: Removed the else-if block that was updating token object references
            // This was causing an infinite loop because fromToken/toToken were in the dependency array
            // Object reference updates aren't necessary - React handles this fine
        } else {
            console.log('[SwapInterface] backendTokens not loaded yet');
        }
    }, [backendTokens, userTokenPairsList, fromToken, toToken]); // Added fromToken/toToken to dependencies to check if they're set

    // Auto-disable atomic swap if tokens don't support it
    useEffect(() => {
        if (!atomicSwapAvailable && useAtomicSwap) {
            setUseAtomicSwap(false);
        }
    }, [atomicSwapAvailable, useAtomicSwap]);

    // MM2 Manager Handler - PAUSED (can be re-enabled in future)
    // const handleOpenMM2Manager = () => {
    //     if (typeof document !== 'undefined') {
    //         document.getElementById('mm2-wallet-manager')?.scrollIntoView({ behavior: 'smooth' });
    //     }
    // };

    // Check if matching atomic swap offer exists and fetch optimal route
    // Debounced to reduce CPU usage and API calls
    useEffect(() => {
        // Debug: Log whenever this effect runs
        console.log('[SwapInterface] Routing useEffect triggered:', {
            fromToken: fromToken?.symbol || 'null',
            toToken: toToken?.symbol || 'null',
            fromAmount,
            tokensInitialized: tokensInitialized.current
        });
        
        // Debounce route fetching to prevent excessive API calls
        const timeoutId = setTimeout(() => {
            const checkRouting = async () => {
                // Recalculate fromAmountNum inside useEffect to ensure it's current
                const currentFromAmountNum = fromAmount && parseFloat(fromAmount) > 0 ? parseFloat(fromAmount) : null;
                
                // Debug logging to understand why quotes aren't fetching
                console.log('[SwapInterface] checkRouting executing:', {
                    hasFromToken: !!fromToken,
                    hasToToken: !!toToken,
                    fromTokenSymbol: fromToken?.symbol,
                    toTokenSymbol: toToken?.symbol,
                    fromAmount,
                    currentFromAmountNum,
                    condition: !!(fromToken && toToken && currentFromAmountNum && currentFromAmountNum > 0)
                });
                
                if (fromToken && toToken && currentFromAmountNum && currentFromAmountNum > 0) {
                    // Check if this is a UTXO token swap (always fetch quotes for manual swaps)
                    // This allows users to see rates/fees before clicking "Get Deposit Address"
                    const isUTXOToken = fromToken && isAtomicSwapCompatible(fromToken.symbol);
                    
                    // Check for matching MM2 offer if availableOffers is loaded
                    const foundMatchingOffer = availableOffers?.find(
                        offer => 
                            (offer.fromToken === fromToken.symbol && offer.toToken === toToken.symbol) ||
                            (offer.fromToken === toToken.symbol && offer.toToken === fromToken.symbol)
                    ) || null;
                    
                    setMatchingOffer(foundMatchingOffer);
                    const needsFallback = !foundMatchingOffer && !useAtomicSwap;
                    setUseFallbackRouting(needsFallback);
                    const shouldFetchQuotes = needsFallback || isUTXOToken;
                    
                    console.log('[SwapInterface] Quote fetch decision:', {
                        isUTXOToken,
                        foundMatchingOffer: !!foundMatchingOffer,
                        needsFallback,
                        shouldFetchQuotes
                    });

                    // Fetch optimal route (MM2 first, then aggregators)
                    // Quotes don't require wallet - they're just price estimates
                    // Always fetch for UTXO tokens so users can see rates before clicking "Get Deposit Address"
                    if (shouldFetchQuotes) {
                        console.log('[SwapInterface] Fetching quotes...', {
                            fromToken: fromToken.symbol,
                            toToken: toToken.symbol,
                            amount: currentFromAmountNum,
                            isUTXO: isUTXOToken,
                            needsFallback,
                            destinationAddress: destinationAddress?.trim()
                        });
                        setIsFetchingRoute(true);
                        try {
                            // Get all quotes (MM2 first, then aggregators) - use selected chain
                            // Use destinationAddress if provided, even without wallet connection
                            // Pass token chain IDs for multi-chain tokens (e.g., USDT on Ethereum)
                            const result = await getOptimalSwapRoute(
                                fromToken.symbol, 
                                toToken.symbol, 
                                currentFromAmountNum, 
                                selectedChainId, 
                                destinationAddress?.trim() || undefined,
                                userTokenPairsList || undefined,
                                fromToken.chainId,
                                toToken.chainId
                            );
                            console.log('[SwapInterface] Quotes fetched:', {
                                best: result.best?.provider,
                                allCount: result.all.length,
                                availableCount: result.all.filter(q => q.available).length
                            });
                            setOptimalQuote(result.best);
                            setAllQuotes(result.all);
                            
                            // Update toAmount based on quote if available
                            if (result.best && result.best.available && fromAmount && parseFloat(fromAmount) > 0) {
                                const estimatedOutput = result.best.estimatedOutput || 0;
                                if (estimatedOutput > 0 && document.activeElement !== toAmountInputRef.current) {
                                    setToAmount(estimatedOutput.toFixed(8));
                                }
                            }
                        } catch (error) {
                            console.error('[SwapInterface] Failed to fetch optimal route:', error);
                            setOptimalQuote(null);
                            setAllQuotes([]);
                        } finally {
                            setIsFetchingRoute(false);
                        }
                    } else {
                        setOptimalQuote(null);
                        setAllQuotes([]);
                    }
                } else {
                    setUseFallbackRouting(false);
                    setOptimalQuote(null);
                    setAllQuotes([]);
                    setMatchingOffer(null);
                }
            };

            checkRouting();
        }, 500); // 500ms debounce to reduce CPU and API calls

        return () => clearTimeout(timeoutId);
    }, [fromToken, toToken, fromAmount, availableOffers, useAtomicSwap, selectedChainId, destinationAddress]);

    // Calculate chain IDs immediately from token selection (industry standard)
    // This allows destination address field to appear immediately, not waiting for quotes
    // MUST be defined before any useEffect that uses needsDestinationAddress
    // CRITICAL: Extract userTokenPairsList to local variable to prevent bundler hoisting issues
    const localCustomTokenPairs = userTokenPairsList;
    // Use token's chainId if available (for multi-chain tokens), otherwise fall back to primary chain
    const calculatedFromChainId = fromToken 
        ? (fromToken.chainId || getTokenPrimaryChain(fromToken.symbol, localCustomTokenPairs) || selectedChainId)
        : selectedChainId;
        
    const calculatedToChainId = toToken
        ? (toToken.chainId || getTokenPrimaryChain(toToken.symbol, localCustomTokenPairs) || selectedChainId)
        : selectedChainId;

    // Use quote chain IDs if available (more accurate), otherwise use calculated
    // This gives us the best of both worlds: immediate UI + accurate validation
    const effectiveFromChainId = optimalQuote?.fromChainId || calculatedFromChainId;
    const effectiveToChainId = optimalQuote?.toChainId || calculatedToChainId;

    // Check if destination address is required (must be defined before any useEffect that uses it)
    // For aggregators, destination address is required even without wallet connection
    const needsDestinationAddress = fromToken && toToken
        ? requiresDestinationAddress(
            fromToken.symbol,
            toToken.symbol,
            walletType || 'web3',
            effectiveFromChainId,
            effectiveToChainId,
            userTokenPairsList || undefined
        )
        : false;
    
    // Debug logging for destination address field
    if (import.meta.env.DEV && fromToken && toToken) {
        console.log('[SwapInterface] Destination Address Debug:', {
            fromToken: fromToken.symbol,
            toToken: toToken.symbol,
            isConnected,
            walletType: walletType || 'web3',
            effectiveFromChainId,
            effectiveToChainId,
            needsDestinationAddress,
            calculatedFromChainId,
            calculatedToChainId
        });
    }
    
    const destinationAddressFormat = fromToken && toToken
        ? getDestinationAddressFormat(toToken.symbol, effectiveToChainId, userTokenPairsList || undefined)
        : '';
    
    const destinationAddressReason = fromToken && toToken && isConnected
        ?             getDestinationAddressRequirementReason(
              fromToken.symbol,
              toToken.symbol,
              walletType || 'web3',
              effectiveFromChainId,
              effectiveToChainId,
              userTokenPairsList || undefined
            )
        : '';

    // Validate destination address (must be defined before useEffects that use it)
    // Wrapped in useCallback to prevent unnecessary re-renders and ensure stable reference
    const validateDestinationAddress = useCallback((address: string): boolean => {
        if (!needsDestinationAddress) {
            setDestinationAddressError('');
            return true;
        }
        
        if (!address || address.trim().length === 0) {
            setDestinationAddressError('Destination address is required for cross-chain swaps');
            return false;
        }
        
        // Validate address format for destination chain (use effective chain ID)
        // Pass token symbol for special cases like Monero (XMR) which doesn't have a chain ID
        const chainIdToValidate = effectiveToChainId || calculatedToChainId;
        if (chainIdToValidate || toToken?.symbol) {
            const isValid = validateAddressForChain(
                address.trim(), 
                chainIdToValidate || 0, // Use 0 as fallback if no chain ID
                toToken?.symbol // Pass token symbol for Monero validation
            );
            if (!isValid) {
                setDestinationAddressError(`Invalid address format. Expected: ${destinationAddressFormat}`);
                return false;
            }
        }
        
        setDestinationAddressError('');
        return true;
    }, [needsDestinationAddress, effectiveToChainId, calculatedToChainId, toToken, destinationAddressFormat]);

    // Clear quotes when chain changes
    // BUT: Don't clear destination address if user has entered a value
    useEffect(() => {
        setOptimalQuote(null);
        setAllQuotes([]);
        setUseFallbackRouting(false);
        
        // Only clear destination address if tokens actually changed AND user hasn't entered anything
        const fromTokenChanged = prevFromTokenRef.current?.symbol !== fromToken?.symbol;
        const toTokenChanged = prevToTokenRef.current?.symbol !== toToken?.symbol;
        const inputFocused = document.activeElement === destinationAddressInputRef.current;
        
        // Only clear if tokens changed AND user hasn't manually entered an address AND input is not focused
        if ((fromTokenChanged || toTokenChanged) && !userHasEnteredAddressRef.current && !isUserTypingRef.current && !inputFocused) {
            setDestinationAddress('');
            setDestinationAddressError('');
            userHasEnteredAddressRef.current = false;
        }
        
        // If tokens changed and user has entered an address, re-validate with new token format
        // This ensures validation runs with the correct address format for the new token
        if ((fromTokenChanged || toTokenChanged) && destinationAddress && needsDestinationAddress) {
            // Small delay to ensure destinationAddressFormat has updated
            setTimeout(() => {
                validateDestinationAddress(destinationAddress);
            }, 0);
        }
        
        // Update refs
        prevFromTokenRef.current = fromToken;
        prevToTokenRef.current = toToken;
    }, [fromToken, toToken, destinationAddress, needsDestinationAddress, validateDestinationAddress]);
    
    // Clear destination address when tokens change or when not needed
    // BUT: Don't clear if user is actively typing (check if address was just set)
    const destinationAddressRef = useRef(destinationAddress);
    useEffect(() => {
        destinationAddressRef.current = destinationAddress;
    }, [destinationAddress]);
    
    useEffect(() => {
        // Only clear if needsDestinationAddress becomes false AND address wasn't just set by user
        // This prevents clearing while user is typing
        if (!needsDestinationAddress && !destinationAddressRef.current) {
            setDestinationAddress('');
            setDestinationAddressError('');
        }
    }, [needsDestinationAddress]);
    
    // Validate destination address whenever it changes
    useEffect(() => {
        if (needsDestinationAddress && destinationAddress) {
            // Call validation directly to avoid circular dependency
            const isValid = validateDestinationAddress(destinationAddress);
            // Validation function already sets error state, so we don't need to do anything else
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationAddress, needsDestinationAddress]);

    const calculateExchangeRate = () => {
        if (!fromToken || !toToken) return 0;
        
        // Use quote data if available (more accurate than price data)
        if (optimalQuote && optimalQuote.available && fromAmount && parseFloat(fromAmount) > 0) {
            const quoteOutput = optimalQuote.estimatedOutput || 0;
            if (quoteOutput > 0) {
                const rate = quoteOutput / parseFloat(fromAmount);
                if (isFinite(rate) && !isNaN(rate) && rate > 0) {
                    return rate;
                }
            }
        }
        
        // Fallback to price data if quote not available
        const fromPrice = livePrices[fromToken.symbol] || 0;
        const toPrice = livePrices[toToken.symbol] || 0;
        // Only use price data if both prices are available (don't default to 1)
        if (fromPrice === 0 || toPrice === 0) return 0;
        const rate = fromPrice / toPrice;
        // Check for invalid values
        if (!isFinite(rate) || isNaN(rate)) return 0;
        return rate;
    };

    // Format exchange rate with appropriate precision (always avoid scientific notation)
    const formatExchangeRate = (rate: number): string => {
        if (!isFinite(rate) || isNaN(rate) || rate <= 0) return '0.00000000';
        
        // For very small rates, use toFixed with enough decimal places to avoid scientific notation
        if (rate < 0.0001) {
            // Calculate how many decimal places needed (at least 12 to avoid scientific notation)
            const magnitude = Math.floor(Math.log10(rate));
            const decimalPlaces = Math.max(12, -magnitude + 4);
            return rate.toFixed(decimalPlaces);
        }
        
        // For larger rates, use fixed 8 decimal places
        return rate.toFixed(8);
    };

    // Calculate price impact based on swap amount and liquidity
    const calculatePriceImpact = (): number => {
        if (!fromToken || !toToken || !fromAmount || !optimalQuote) return 0;
        
        const amount = parseFloat(fromAmount);
        if (amount <= 0) return 0;
        
        // Simple price impact calculation
        // In production, this would use actual liquidity pool data
        const fromPrice = livePrices[fromToken.symbol] || 1;
        const toPrice = livePrices[toToken.symbol] || 1;
        const valueUSD = amount * fromPrice;
        
        // Estimate price impact (higher for larger swaps)
        // This is a simplified calculation - real DEX would use AMM formulas
        const estimatedImpact = Math.min(valueUSD / 10000, 5); // Cap at 5%
        
        return estimatedImpact;
    };

    const handleFromAmountChange = (value: string) => {
        // Mark that user is typing
        userIsTypingAmountRef.current = true;
        if (value.trim().length > 0) {
            userHasEnteredAmountRef.current = true;
        }
        
        // Only allow valid number inputs
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setFromAmount(value);
            if (value && !isNaN(parseFloat(value)) && parseFloat(value) > 0) {
                const rate = calculateExchangeRate();
                // Only update toAmount if user is not actively typing in toAmount field
                if (document.activeElement !== toAmountInputRef.current && rate > 0) {
                    setToAmount((parseFloat(value) * rate).toFixed(8));
                }
            } else {
                // Only clear toAmount if user cleared fromAmount AND is not typing in toAmount
                if (value === '' && document.activeElement !== toAmountInputRef.current) {
                    setToAmount('');
                }
                // If user clears the field, reset the flag
                if (value.length === 0) {
                    userHasEnteredAmountRef.current = false;
                }
            }
        }
        
        // Reset typing flag after delay (cleanup previous timeout)
        if (fromAmountTypingTimeoutRef.current) {
            clearTimeout(fromAmountTypingTimeoutRef.current);
        }
        fromAmountTypingTimeoutRef.current = setTimeout(() => {
            if (document.activeElement !== fromAmountInputRef.current) {
                userIsTypingAmountRef.current = false;
            }
            fromAmountTypingTimeoutRef.current = null;
        }, 2000);
    };

    const handleToAmountChange = (value: string) => {
        // Mark that user is typing
        userIsTypingAmountRef.current = true;
        if (value.trim().length > 0) {
            userHasEnteredAmountRef.current = true;
        }
        
        // Only allow valid number inputs
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setToAmount(value);
            if (value && !isNaN(parseFloat(value)) && parseFloat(value) > 0) {
                const rate = calculateExchangeRate();
                // Only update fromAmount if user is not actively typing in fromAmount field
                if (document.activeElement !== fromAmountInputRef.current && rate > 0) {
                    setFromAmount((parseFloat(value) / rate).toFixed(8));
                }
            } else {
                // Only clear fromAmount if user cleared toAmount AND is not typing in fromAmount
                if (value === '' && document.activeElement !== fromAmountInputRef.current) {
                    setFromAmount('');
                }
                // If user clears the field, reset the flag
                if (value.length === 0) {
                    userHasEnteredAmountRef.current = false;
                }
            }
        }
        
        // Reset typing flag after delay (cleanup previous timeout)
        if (toAmountTypingTimeoutRef.current) {
            clearTimeout(toAmountTypingTimeoutRef.current);
        }
        toAmountTypingTimeoutRef.current = setTimeout(() => {
            if (document.activeElement !== toAmountInputRef.current) {
                userIsTypingAmountRef.current = false;
            }
            toAmountTypingTimeoutRef.current = null;
        }, 2000);
    };

    const handleSwapTokens = () => {
        // Only swap if both tokens are selected
        if (!fromToken || !toToken) {
            console.log('[SwapInterface] Cannot swap: missing tokens');
            return;
        }
        
        // Capture current values
        const currentFromToken = fromToken;
        const currentToToken = toToken;
        const currentFromAmount = fromAmount;
        const currentToAmount = toAmount;
        
        console.log('[SwapInterface] handleSwapTokens - BEFORE swap:', {
            from: currentFromToken.symbol,
            to: currentToToken.symbol,
            fromAmount: currentFromAmount,
            toAmount: currentToAmount
        });
        
        // Immediately persist to localStorage to prevent remount from restoring old values
        try {
            if (currentToToken.symbol) {
                localStorage.setItem(STORAGE_KEYS.fromToken, currentToToken.symbol);
                if (currentToToken.chainId) {
                    localStorage.setItem(STORAGE_KEYS.fromTokenChainId, currentToToken.chainId.toString());
                } else {
                    localStorage.removeItem(STORAGE_KEYS.fromTokenChainId);
                }
            }
            if (currentFromToken.symbol) {
                localStorage.setItem(STORAGE_KEYS.toToken, currentFromToken.symbol);
                if (currentFromToken.chainId) {
                    localStorage.setItem(STORAGE_KEYS.toTokenChainId, currentFromToken.chainId.toString());
                } else {
                    localStorage.removeItem(STORAGE_KEYS.toTokenChainId);
                }
            }
            if (currentToAmount) {
                localStorage.setItem(STORAGE_KEYS.fromAmount, currentToAmount);
            }
            if (currentFromAmount) {
                localStorage.setItem(STORAGE_KEYS.toAmount, currentFromAmount);
            }
        } catch (e) {
            // Silently fail if localStorage is not available
        }
        
        // Swap tokens and amounts - React will batch these updates
        setFromToken(currentToToken);
        setToToken(currentFromToken);
        setFromAmount(currentToAmount);
        setToAmount(currentFromAmount);
        
        console.log('[SwapInterface] handleSwapTokens - AFTER state updates called');
    };

    const handleSelectOffer = (offer: SwapOffer) => {
        // Find tokens from the offer
        const fromTokenObj = tokens.find(t => t.symbol === offer.fromToken);
        const toTokenObj = tokens.find(t => t.symbol === offer.toToken);

        if (fromTokenObj && toTokenObj) {
            setFromToken(toTokenObj); // User wants what the offer is requesting
            setToToken(fromTokenObj); // User will provide what the offer is offering
            setFromAmount(offer.toAmount.toString());
            setToAmount(offer.fromAmount.toString());
            toast.success(`Prefilled swap from offer: ${offer.fromToken}/${offer.toToken}`);
        } else {
            toast.error('Could not find tokens from the selected offer');
        }
    };

    const handleAssetsNFTs = () => {
        const assetsSection = document.getElementById('assets-nfts-section');
        if (assetsSection) {
            assetsSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleSwap = async () => {
        // Check if wallet is required for this provider
        const needsWallet = optimalQuote && requiresWalletSigning(optimalQuote.provider);
        
        if (needsWallet && !isConnected) {
            toast.error('Wallet Required', {
                description: 'This swap requires a connected wallet to sign transactions. Please connect your wallet.',
                duration: 5000,
            });
            return;
        }
        
        // For aggregators, only require destination address
        if (optimalQuote && !requiresWalletSigning(optimalQuote.provider)) {
            if (needsDestinationAddress && !destinationAddress) {
                toast.error('Destination Address Required', {
                    description: 'Please enter the destination address where you want to receive the tokens.',
                    duration: 5000,
                });
                return;
            }
        }

        if (!fromToken || !toToken) {
            toast.error('Tokens Not Selected', {
                description: 'Please select both the token you want to swap from and the token you want to swap to',
                duration: 5000,
            });
            return;
        }

        if (!fromAmount || parseFloat(fromAmount) <= 0) {
            toast.error('Invalid Amount', {
                description: 'Please enter a valid amount greater than zero',
                duration: 5000,
            });
            return;
        }

        // Check if user has sufficient balance
        // For UTXO-style manual swaps, ALWAYS skip balance check – user sends from their own wallet to a deposit address
        const isUTXOToken = isAtomicSwapCompatible(fromToken.symbol);

        if (!isUTXOToken) {
            // SKIP balance check for UTXO tokens (RVN, BTC, etc.) – they use manual deposit addresses
            const userBalance = balances[fromToken.symbol] || 0;
            const amountNum = parseFloat(fromAmount);
            const isWalletConnect = walletType === 'web3'; // Trust Wallet uses 'web3' type

            // Debug log so we can see what's happening in browser console
            if (import.meta.env.DEV) {
                console.log('[SwapInterface] Balance check', {
                    fromToken: fromToken.symbol,
                    amountNum,
                    userBalance,
                    walletType,
                    isWalletConnect,
                });
            }

            if (amountNum > userBalance) {
                // For EVM tokens or when balance is known, block if insufficient
                toast.error('Insufficient Balance', {
                    description: `You have ${userBalance.toFixed(6)} ${fromToken.symbol}, but trying to swap ${fromAmount} ${fromToken.symbol}`,
                    duration: 8000,
                });
                return;
            }
        }

        // Check if tokens are the same
        if (fromToken.symbol === toToken.symbol) {
            toast.error('Invalid Swap', {
                description: 'Cannot swap a token for itself. Please select different tokens.',
                duration: 5000,
            });
            return;
        }

        // Check for cross-chain swap and prompt chain switch if needed
        // Skip chain switching for UTXO tokens (10000-10007) - these require manual deposit
        if (optimalQuote?.isCrossChain && fromToken && toToken) {
            const { getTokenChains, getTokenPrimaryChain } = await import('../lib/tokenRegistry');
            // CRITICAL: Use local variable to prevent bundler hoisting issues
            const localCustomTokenPairs = userTokenPairsList;
            const fromChains = getTokenChains(fromToken.symbol, localCustomTokenPairs);
            // Use token's chainId if available (for multi-chain tokens), otherwise use primary chain
            const requiredChain = fromToken.chainId || getTokenPrimaryChain(fromToken.symbol, localCustomTokenPairs) || fromChains[0];
            
            // UTXO chain IDs (10000-10007) don't support chain switching via WalletConnect
            // For these, we proceed with the swap and the aggregator will return a deposit address
            const isUTXOChain = requiredChain && requiredChain >= 10000 && requiredChain <= 10007;
            
            if (walletType === 'web3' && networkInfo && requiredChain && !isUTXOChain) {
                const walletChainId = parseInt(networkInfo.chainId, 16);
                if (walletChainId !== requiredChain) {
                    const shouldSwitch = window.confirm(
                        `This is a cross-chain swap. Your wallet is on chain ${walletChainId}, but ${fromToken.symbol} requires chain ${requiredChain}. Switch your wallet to the correct chain?`
                    );
                    if (!shouldSwitch) {
                        toast.info('Chain Switch Required', {
                            description: 'Please switch your wallet to the correct chain to proceed with this cross-chain swap.',
                            duration: 6000,
                        });
                        return;
                    }
                    // User confirmed - they'll need to switch manually
                    toast.info('Switch Your Wallet', {
                        description: `Please switch your wallet to chain ${requiredChain} to continue.`,
                        duration: 8000,
                    });
                    return;
                }
            }
        }

        // Validate wallet chain matches selected chain (for EVM wallets only)
        if (walletType === 'web3' && networkInfo) {
            // Chain ID validation removed - routing is now automatic
        }

        // Warn about high price impact
        if (priceImpact > 1) {
            const shouldProceed = window.confirm(
                `Warning: This swap may result in ${priceImpact.toFixed(2)}% price impact. This is a large swap that may significantly affect the price. Do you want to proceed?`
            );
            if (!shouldProceed) {
                return;
            }
        }

        if (useAtomicSwap) {
            toast.info('Atomic swap mode - please use the Atomic Swap interface below');
            // Scroll to atomic swap section
            const atomicSwapSection = document.getElementById('atomic-swap-section');
            if (atomicSwapSection) {
                atomicSwapSection.scrollIntoView({ behavior: 'smooth' });
            }
            return;
        }

        // Validate destination address if required
        if (needsDestinationAddress) {
            if (!validateDestinationAddress(destinationAddress)) {
                toast.error('Invalid Destination Address', {
                    description: destinationAddressError || 'Please enter a valid destination address for the cross-chain swap.',
                    duration: 6000,
                });
                return;
            }
        }

        // Show transaction modal and start swap process
        setShowTransactionModal(true);
        setTransactionStatus('confirming');
        setErrorMessage('');

        // Brief confirmation state for UX (reduced from 1500ms to 300ms)
        await new Promise(resolve => setTimeout(resolve, 300));
        
        setTransactionStatus('processing');

        // CRITICAL: Check if this is UTXO and try direct wallet signing FIRST (before checking for quotes)
        // This allows UTXO signing to work even when aggregators fail or require larger amounts
        const isUTXOTokenForSigning = isAtomicSwapCompatible(fromToken.symbol);
        
        // DEBUG: Log UTXO signing check
        console.log('[SwapInterface] UTXO Signing Check:', {
            fromToken: fromToken?.symbol,
            isUTXOTokenForSigning,
            walletType,
            isConnected,
            walletAddress,
            destinationAddress: destinationAddress?.trim(),
            fromAmount,
            willAttemptUTXOSigning: isUTXOTokenForSigning && ((walletType === 'web3' && isConnected && walletAddress) || (walletType === 'ravencoin' && isConnected && walletAddress))
        });
        
        // Note: Ravencoin Core transaction signing would go here if needed
        // For now, UTXO tokens use WalletConnect signing (bip122) or aggregators
        
        // Try WalletConnect UTXO signing (for all UTXO tokens via WalletConnect)
        if (isUTXOTokenForSigning && walletType === 'web3' && isConnected && walletAddress) {
            console.log('[SwapInterface] WalletConnect UTXO signing path entered - starting UTXO transaction signing');
            try {
                // For UTXO signing, we need a destination address
                if (!destinationAddress?.trim()) {
                    console.error('[SwapInterface] UTXO signing failed: No destination address');
                    throw new Error('Destination address required for UTXO transaction signing');
                }
                
                console.log('[SwapInterface] Connecting UTXO provider...');
                // Connect UTXO provider if not already connected
                await connectUTXOProvider();
                console.log('[SwapInterface] UTXO provider connected');
                
                // Log wallet capabilities for debugging (especially for Trust Wallet testing)
                logWalletCapabilities();
                
                console.log('[SwapInterface] Checking UTXO support...');
                const hasUTXOSupport = checkUTXOSupport();
                console.log('[SwapInterface] UTXO support check result:', hasUTXOSupport);
                
                if (hasUTXOSupport) {
                    // Wallet supports UTXO signing - use it!
                    console.log('[SwapInterface] Attempting UTXO signing for', fromToken.symbol);
                    
                    const signedTx = await signUTXOTransaction(
                        fromToken.symbol,
                        walletAddress,
                        destinationAddress.trim(),
                        parseFloat(fromAmount)
                    );
                    
                    console.log('[SwapInterface] UTXO transaction signed:', signedTx);
                    
                    toast.success('Transaction Signed', {
                        description: `${fromToken.symbol} transaction signed successfully. TX: ${signedTx.txHash ? signedTx.txHash.substring(0, 20) : 'N/A'}...`,
                        duration: 10000,
                    });
                    
                    setTransactionStatus('success');
                    
                    // Auto-close modal after success
                    setTimeout(() => {
                        setShowTransactionModal(false);
                        setTransactionStatus('idle');
                        setFromAmount('');
                        setToAmount('');
                        setDestinationAddress('');
                        setDestinationAddressError('');
                    }, 2000);
                    
                    return; // Exit - transaction signed directly via wallet
                }
            } catch (utxoError: any) {
                // If UTXO signing fails, fall back to aggregator
                console.warn('[SwapInterface] UTXO signing failed, using aggregator:', utxoError);
                toast.info('Using Aggregator', {
                    description: `Wallet doesn't support ${fromToken.symbol} signing. Using aggregator instead.`,
                    duration: 5000,
                });
                // Continue to aggregator flow below
            }
        }

        // For UTXO tokens without a quote, try to fetch one first
        // For manual swaps, we should always try to get a quote, but can still execute without one
        const isUTXOTokenForQuote = fromToken && isAtomicSwapCompatible(fromToken.symbol);
        let quoteToUse = optimalQuote;
        
        // Always try to fetch a quote for UTXO tokens when executing (not just when useFallbackRouting is enabled)
        if (!quoteToUse && isUTXOTokenForQuote && needsDestinationAddress && destinationAddress?.trim()) {
            // Try to fetch a quote for UTXO tokens
            console.log('[SwapInterface] No quote available, fetching quote for UTXO token...');
            setIsFetchingRoute(true);
            try {
                const result = await getOptimalSwapRoute(
                    fromToken.symbol,
                    toToken.symbol,
                    parseFloat(fromAmount),
                    selectedChainId,
                    destinationAddress?.trim() || undefined,
                    userTokenPairsList || undefined,
                    fromToken.chainId,
                    toToken.chainId
                );
                quoteToUse = result.best;
                if (quoteToUse) {
                    setOptimalQuote(quoteToUse);
                    console.log('[SwapInterface] Quote fetched successfully:', quoteToUse.provider);
                } else {
                    console.warn('[SwapInterface] No quote available from aggregators - will try direct execution for manual swaps');
                    // Don't error out - we'll try direct execution below
                }
            } catch (error: any) {
                console.error('[SwapInterface] Failed to fetch quote:', error);
                // Don't error out - we'll try direct execution below
                console.log('[SwapInterface] Will attempt direct execution with manual swap providers');
            } finally {
                setIsFetchingRoute(false);
            }
        }
        
        // Execute swap via MM2 or aggregator if we have an optimal quote
        if (quoteToUse) {
            try {
                // For aggregators, use destinationAddress; for signing providers, use walletAddress
                const needsWallet = requiresWalletSigning(quoteToUse.provider);
                const effectiveAddress = needsWallet
                    ? (walletAddress || '')
                    : (destinationAddress?.trim() || walletAddress || ''); // Fallback to walletAddress if destination not provided
                
                if (needsWallet && !effectiveAddress) {
                    throw new Error('Wallet address required for this swap');
                }
                
                if (!needsWallet && needsDestinationAddress && !destinationAddress?.trim()) {
                    throw new Error('Destination address required for this swap');
                }
                
                // Ensure we have a valid address string
                if (!effectiveAddress) {
                    throw new Error('Address required for swap execution');
                }
                
                // Debug log before executing swap
                console.log('[SwapInterface] Executing swap with:', {
                    provider: quoteToUse.provider,
                    fromToken: fromToken.symbol,
                    toToken: toToken.symbol,
                    amount: parseFloat(fromAmount),
                    effectiveAddress,
                    destinationAddress: needsDestinationAddress ? destinationAddress.trim() : undefined,
                    needsDestinationAddress,
                });

                const result = await executeSwapRoute(
                    quoteToUse,
                    fromToken.symbol,
                    toToken.symbol,
                    parseFloat(fromAmount),
                    walletAddress || '', // Pass actual wallet address, not effectiveAddress
                    selectedChainId,
                    needsDestinationAddress ? destinationAddress.trim() : undefined
                );

                if (result.success) {
                    // Check if deposit address is required (for aggregators like SimpleSwap, StealthEX, ChangeNOW)
                    console.log('[SwapInterface] Swap result:', {
                        success: result.success,
                        depositAddress: result.depositAddress,
                        exchangeId: result.exchangeId,
                        provider: optimalQuote.provider,
                        fromToken: fromToken.symbol,
                        toToken: toToken.symbol,
                    });
                    
                    if (result.depositAddress) {
                        console.log('[SwapInterface] Deposit address received from provider:', {
                            depositAddress: result.depositAddress,
                            provider: quoteToUse.provider,
                            exchangeId: result.exchangeId,
                        });
                        
                        // Verify deposit address is from the actual provider (not hardcoded)
                        if (!result.depositAddress || result.depositAddress.length < 10) {
                            console.error('[SwapInterface] Invalid deposit address received:', result.depositAddress);
                            throw new Error('Invalid deposit address received from provider');
                        }
                        
                        // Show deposit address modal
                        setDepositInfo({
                            depositAddress: result.depositAddress,
                            amount: parseFloat(fromAmount),
                            token: fromToken.symbol,
                            exchangeId: result.exchangeId,
                            provider: quoteToUse.provider,
                            destinationAddress: needsDestinationAddress ? destinationAddress.trim() : undefined,
                            toToken: toToken.symbol,
                            payinExtraId: result.payinExtraId,
                        });
                        setShowDepositModal(true);
                        setShowTransactionModal(false);
                        setTransactionStatus('idle');
                        
                        // Also show a toast with the address as backup
                        toast.success('Deposit Address Ready', {
                            description: `Send ${fromAmount} ${fromToken.symbol} to the address shown in the modal. This address is from ${quoteToUse.provider}.`,
                            duration: 10000,
                        });
                    } else {
                        console.log('[SwapInterface] No deposit address - standard EVM swap');
                        // Standard EVM swap - no deposit address needed
                    setTransactionStatus('success');
                    
                    // Auto-close modal after success
                    setTimeout(() => {
                        setShowTransactionModal(false);
                        setTransactionStatus('idle');
                        setFromAmount('');
                        setToAmount('');
                        setDestinationAddress('');
                        setDestinationAddressError('');
                        toast.success(`Successfully swapped ${fromAmount} ${fromToken.symbol} for ${toAmount} ${toToken.symbol}`, {
                            description: `Transaction: ${result.txHash.substring(0, 10)}...`,
                            duration: 8000,
                        });
                    }, 2000);
                    }
                } else {
                    throw new Error('Swap execution failed');
                }
            } catch (error: any) {
                setTransactionStatus('error');
                console.error('[SwapInterface] Swap execution error:', error);
                const errorMsg = getSafeErrorMessage(error, 'Transaction failed');
                setErrorMessage(errorMsg);
                
                // Show detailed error message to help debug
                const detailedError = error?.message || errorMsg;
                toast.error('Swap Failed', {
                    description: detailedError,
                    duration: 8000,
                });
            }
        } else if (!quoteToUse && isUTXOTokenForQuote && needsDestinationAddress && destinationAddress?.trim()) {
            // For UTXO manual swaps without a quote, try direct execution with manual swap providers
            // This allows users to get deposit addresses even when quote fetching fails
            console.log('[SwapInterface] No quote available, attempting direct execution with manual swap providers...');
            try {
                // First, try to get quotes from all manual swap providers to find the best one
                const { fetchSimpleSwapQuote, fetchChangeNOWQuote, MANUAL_SWAP_PROVIDERS } = await import('../lib/liquidityProviders');
                const manualSwapProviders = [...MANUAL_SWAP_PROVIDERS];
                
                console.log('[SwapInterface] Fetching quotes from all manual swap providers to find best option...');
                const quotePromises = [
                    fetchSimpleSwapQuote(fromToken.symbol, toToken.symbol, parseFloat(fromAmount), destinationAddress.trim()).then(q => ({ provider: 'SimpleSwap', quote: q })),
                    fetchChangeNOWQuote(fromToken.symbol, toToken.symbol, parseFloat(fromAmount)).then(q => ({ provider: 'ChangeNOW', quote: q })), // ✅ ACTIVE - Requires VPS proxy (fixed)
                ];
                
                const quoteResults = await Promise.allSettled(quotePromises);
                const availableQuotes = quoteResults
                    .filter((result): result is PromiseFulfilledResult<{ provider: string; quote: any }> => 
                        result.status === 'fulfilled' && result.value.quote.available && result.value.quote.estimatedOutput > 0
                    )
                    .map(result => result.value);
                
                // Select best quote (highest estimated output)
                let bestProvider: string | null = null;
                if (availableQuotes.length > 0) {
                    const best = availableQuotes.reduce((best, current) =>
                        current.quote.estimatedOutput > best.quote.estimatedOutput ? current : best
                    );
                    bestProvider = best.provider;
                    console.log(`[SwapInterface] Best manual swap provider: ${bestProvider} (output: ${best.quote.estimatedOutput})`);
                } else {
                    // If no quotes available, fall back to trying providers in order
                    console.log('[SwapInterface] No quotes available, trying providers in order...');
                    bestProvider = manualSwapProviders[0];
                }
                
                // Execute with the best provider (or first provider if no quotes)
                if (bestProvider) {
                    console.log(`[SwapInterface] Executing with ${bestProvider}...`);
                    const { executeProviderSwap } = await import('../lib/liquidityProviders');
                    
                    const result = await executeProviderSwap(
                        bestProvider,
                        fromToken.symbol,
                        toToken.symbol,
                        parseFloat(fromAmount),
                        walletAddress || '',
                        selectedChainId,
                        calculatedToChainId || selectedChainId,
                        destinationAddress.trim()
                    );
                    
                    if (result.success && result.depositAddress) {
                        console.log(`[SwapInterface] Successfully got deposit address from ${bestProvider}:`, result.depositAddress);
                        
                        // Show deposit address modal
                        setDepositInfo({
                            depositAddress: result.depositAddress,
                            amount: parseFloat(fromAmount),
                            token: fromToken.symbol,
                            exchangeId: result.exchangeId,
                            provider: bestProvider,
                            destinationAddress: destinationAddress.trim(),
                            toToken: toToken.symbol,
                            payinExtraId: result.payinExtraId,
                        });
                        setShowDepositModal(true);
                        setShowTransactionModal(false);
                        setTransactionStatus('idle');
                        
                        toast.success('Deposit Address Ready', {
                            description: `Send ${fromAmount} ${fromToken.symbol} to the address shown in the modal. Best route: ${bestProvider}.`,
                            duration: 10000,
                        });
                        return; // Success - exit function
                    }
                }
                
                // If execution failed, try other providers as fallback
                for (const provider of manualSwapProviders) {
                    if (provider === bestProvider) continue; // Already tried
                    
                    try {
                        console.log(`[SwapInterface] Trying fallback provider ${provider}...`);
                        const { executeProviderSwap } = await import('../lib/liquidityProviders');
                        
                        const result = await executeProviderSwap(
                            provider,
                            fromToken.symbol,
                            toToken.symbol,
                            parseFloat(fromAmount),
                            walletAddress || '',
                            selectedChainId,
                            calculatedToChainId || selectedChainId,
                            destinationAddress.trim()
                        );
                        
                        if (result.success && result.depositAddress) {
                            console.log(`[SwapInterface] Successfully got deposit address from ${provider}:`, result.depositAddress);
                            
                            setDepositInfo({
                                depositAddress: result.depositAddress,
                                amount: parseFloat(fromAmount),
                                token: fromToken.symbol,
                                exchangeId: result.exchangeId,
                                provider: provider,
                                destinationAddress: destinationAddress.trim(),
                                toToken: toToken.symbol,
                                payinExtraId: result.payinExtraId,
                            });
                            setShowDepositModal(true);
                            setShowTransactionModal(false);
                            setTransactionStatus('idle');
                            
                            toast.success('Deposit Address Ready', {
                                description: `Send ${fromAmount} ${fromToken.symbol} to the address shown in the modal. This address is from ${provider}.`,
                                duration: 10000,
                            });
                            return; // Success - exit function
                        }
                    } catch (providerError: any) {
                        console.warn(`[SwapInterface] ${provider} failed:`, providerError);
                        // Continue to next provider
                    }
                }
                
                throw new Error('All manual swap providers failed to create transaction');
            } catch (error: any) {
                setTransactionStatus('error');
                console.error('[SwapInterface] Direct execution error:', error);
                const errorMsg = getSafeErrorMessage(error, 'Transaction failed');
                setErrorMessage(errorMsg);
                
                toast.error('Swap Failed', {
                    description: errorMsg,
                    duration: 8000,
                });
            }
        } else {
            // Fallback to backend swap execution
            executeSwap(
                {
                    fromToken: fromToken.symbol,
                    toToken: toToken.symbol,
                    amount: parseFloat(fromAmount),
                    routedVia: useFallbackRouting && optimalQuote ? optimalQuote.provider : null,
                },
                {
                    onSuccess: (txId) => {
                        setTransactionStatus('success');
                        
                        // Auto-close modal after success
                        setTimeout(() => {
                            setShowTransactionModal(false);
                            setTransactionStatus('idle');
                            setFromAmount('');
                            setToAmount('');
                            setDestinationAddress('');
                            setDestinationAddressError('');
                            toast.success(`Successfully swapped ${fromAmount} ${fromToken.symbol} for ${toAmount} ${toToken.symbol}`);
                        }, 2000);
                    },
                    onError: (error: any) => {
                        setTransactionStatus('error');
                        const errorMsg = getSafeErrorMessage(error, 'Transaction failed');
                        setErrorMessage(errorMsg);
                        toast.error(errorMsg);
                    },
                }
            );
        }
    };

    const exchangeRate = calculateExchangeRate();
    const fromTokenBalance = fromToken ? (balances[fromToken.symbol] || 0) : 0;
    const toTokenBalance = toToken ? (balances[toToken.symbol] || 0) : 0;
    const priceImpact = calculatePriceImpact();
    
    // Expose test helpers for browser automation
    useEffect(() => {
        if (typeof window !== 'undefined') {
            (window as any).__swapInterfaceTestHelpers = {
                setFromAmount: (value: string) => {
                    handleFromAmountChange(value);
                },
                setDestinationAddress: (value: string) => {
                    setDestinationAddress(value);
                    if (value.trim().length > 0) {
                        userHasEnteredAddressRef.current = true;
                    }
                },
                fillForm: (params: { fromAmount?: string; destinationAddress?: string }) => {
                    if (params.fromAmount) {
                        handleFromAmountChange(params.fromAmount);
                    }
                    if (params.destinationAddress) {
                        setDestinationAddress(params.destinationAddress);
                        userHasEnteredAddressRef.current = true;
                    }
                },
                getState: () => ({
                    fromToken: fromToken?.symbol,
                    toToken: toToken?.symbol,
                    fromAmount,
                    toAmount,
                    destinationAddress,
                    optimalQuote: optimalQuote ? {
                        provider: optimalQuote.provider,
                        estimatedOutput: optimalQuote.estimatedOutput
                    } : null
                })
            };
        }
    }, [fromToken, toToken, fromAmount, toAmount, destinationAddress, optimalQuote]);
    
    // Debug logging for ICP balances
    if (import.meta.env.DEV && isConnected && walletType === 'icp') {
        console.log('[SwapInterface] ICP Wallet balances:', balances);
        console.log('[SwapInterface] From token:', fromToken?.symbol, 'Balance:', fromTokenBalance);
        console.log('[SwapInterface] To token:', toToken?.symbol, 'Balance:', toTokenBalance);
    }
    
    // Update destination address validation when it changes
    useEffect(() => {
        if (destinationAddress && needsDestinationAddress) {
            validateDestinationAddress(destinationAddress);
        } else {
            setDestinationAddressError('');
        }
    }, [destinationAddress, needsDestinationAddress, validateDestinationAddress, toToken]);

    // Calculate fee percentages for display
    const dexFeePercent = feeConfig ? (feeConfig.dexFlatFee * 100).toFixed(2) : '0.40';
    const lpRewardPercent = feeConfig ? (feeConfig.liquidityProviderReward * 100).toFixed(2) : '0.20';
    
    // Third-party fee calculation
    const thirdPartyFee = optimalQuote ? optimalQuote.fee : 0;
    const thirdPartyFeePercent = (thirdPartyFee * 100).toFixed(2);
    const thirdPartyFeeAmount = fromAmountNum ? fromAmountNum * thirdPartyFee : 0;

    const getWalletTypeDisplay = () => {
        if (walletType === 'web3') return 'Web3 Wallet';
        if (walletType === 'ravencoin') return 'Ravencoin Wallet';
        if (walletType === 'icp') return 'ICP Wallet';
        if (walletType === 'solana') return 'Solana Wallet';
        if (walletType === 'xrp') return 'XRP Wallet';
        if (walletType === 'monero') return 'Monero Wallet';
        return 'Wallet';
    };

    return (
        <>
            <section id="swap-section" className="py-16 px-4">
                <div className="container mx-auto max-w-lg">
                    <Card className="border-2 border-border shadow-2xl glow-purple">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-2xl">Swap</CardTitle>
                                <div className="flex items-center gap-2">
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        title="Assets & NFTs"
                                        onClick={handleAssetsNFTs}
                                        className="hover:bg-accent transition-colors"
                                    >
                                        <Coins className="h-5 w-5" />
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        title="Available Swaps"
                                        onClick={() => setShowAvailableSwapsModal(true)}
                                        className="hover:bg-accent transition-colors"
                                    >
                                        <List className="h-5 w-5" />
                                    </Button>
                                    <span 
                                        title={!isConnected ? "Connect wallet to add custom token pairs" : undefined}
                                        className="inline-block"
                                    >
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            title={isConnected ? "Add Custom Token Pair" : undefined}
                                            onClick={() => setShowAddTokenModal(true)}
                                            disabled={!isConnected}
                                            className="hover:bg-accent transition-colors"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </span>
                                    {userTokenPairsList && userTokenPairsList.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            title="Manage Custom Tokens"
                                            onClick={() => setShowCustomTokensManager(true)}
                                            className="hover:bg-accent transition-colors"
                                        >
                                            <Settings className="h-4 w-4" />
                                        </Button>
                                    )}
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        title="Settings"
                                        onClick={() => setShowSettingsModal(true)}
                                        className="hover:bg-accent transition-colors"
                                    >
                                        <Settings className="h-5 w-5" />
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Wallet Connection Notice */}
                            {!isConnected && !optimalQuote && (
                                <Alert className="border-primary/50 bg-primary/10">
                                    <Wallet className="h-4 w-4 text-primary" />
                                    <AlertDescription className="text-sm">
                                        {needsDestinationAddress 
                                            ? "Enter a destination address to see swap quotes. Wallet connection is optional for cross-chain swaps via aggregators."
                                            : "Connect your wallet to start swapping, or enter a destination address for cross-chain swaps."
                                        }
                                    </AlertDescription>
                                </Alert>
                            )}
                            
                            {/* Wallet Required Notice for Signing Providers */}
                            {!isConnected && optimalQuote && requiresWalletSigning(optimalQuote.provider) && (
                                <Alert variant="destructive" className="border-red-500/50 bg-red-500/10">
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                    <AlertDescription className="text-sm text-red-500">
                                        <span className="font-medium">Wallet Required</span> - This swap requires a connected wallet to sign transactions. Please connect your wallet to proceed.
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Monero (XMR) Swap Instructions */}
                            {(fromToken?.symbol === 'XMR' || toToken?.symbol === 'XMR') && (
                                <Alert className="border-orange-500/50 bg-orange-500/10">
                                    <Info className="h-4 w-4 text-orange-500" />
                                    <AlertDescription className="text-sm">
                                        <span className="font-medium text-orange-500">Monero Swap Process:</span><br/>
                                        After confirming, you'll receive a <strong>deposit address</strong>. Manually send XMR from your wallet (Cake Wallet, Monerujo, Monero GUI, etc.) to complete the swap. The exchange typically takes 10-20 minutes after your XMR is received.
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Chain auto-detected from wallet or token type - no manual selection needed */}

                            {/* Wallet Type Display */}
                            {isConnected && (
                                <div className="bg-primary/10 rounded-lg p-3 flex items-center gap-2">
                                    <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                                    <p className="text-sm font-medium">
                                        Connected with {getWalletTypeDisplay()}
                                    </p>
                                </div>
                            )}

                            {/* Quote Display - Show for all swaps (including UTXO manual swaps) */}
                            {(useFallbackRouting || optimalQuote || allQuotes.length > 0) && fromAmount && parseFloat(fromAmount) > 0 && (
                                <>
                                    {isFetchingRoute ? (
                                        <Alert className="border-blue-500/50 bg-blue-500/10">
                                            <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                                            <AlertDescription className="text-blue-500 text-sm">
                                                Finding optimal liquidity route...
                                            </AlertDescription>
                                        </Alert>
                                    ) : optimalQuote ? (
                                        <>
                                            <Alert className="border-yellow-500/50 bg-yellow-500/10">
                                                <ExternalLink className="h-4 w-4 text-yellow-500" />
                                                <AlertDescription className="flex items-center justify-between">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-yellow-500 text-sm font-medium">
                                                            Best Route: {optimalQuote.provider}
                                                        </span>
                                                        {optimalQuote.pathway && (
                                                            <span className="text-yellow-500/90 text-xs font-medium bg-yellow-500/20 px-2 py-0.5 rounded">
                                                                Pathway: {optimalQuote.pathway}
                                                            </span>
                                                        )}
                                                        {optimalQuote.requiresWrapping && optimalQuote.wrappingInstructions && (
                                                            <div className="text-[10px] text-yellow-500/80 bg-yellow-500/10 px-2 py-1 rounded border border-yellow-500/30 mt-1">
                                                                <div className="font-medium mb-0.5">⚠️ Requires Wrapping:</div>
                                                                <div className="whitespace-pre-line leading-tight">{optimalQuote.wrappingInstructions}</div>
                                                            </div>
                                                        )}
                                                        {fromAmountNum && (
                                                            <span className="text-yellow-500/80 text-xs">
                                                                {optimalQuote.estimatedOutput.toFixed(6)} {toToken?.symbol} • {(optimalQuote.fee * 100).toFixed(2)}% fee
                                                            </span>
                                                        )}
                                                        {/* MM2 Orderbook - PAUSED */}
                                                        {/* {optimalQuote.source === 'mm2' && mm2OrderbookData && (
                                                            <span className="text-[10px] text-yellow-500/60">
                                                                MM2 Orderbook: {mm2OrderbookData.bids} bids • {mm2OrderbookData.asks} asks
                                                            </span>
                                                        )} */}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {optimalQuote.source === 'mm2' && (
                                                            <Badge variant="outline" className="text-[10px] border-primary/50 text-primary">
                                                                Atomic
                                                            </Badge>
                                                        )}
                                                        {optimalQuote.kycRisk === 'medium' && (
                                                            <Badge variant="outline" className="text-[10px] border-orange-500/50 text-orange-500" title={optimalQuote.kycMessage}>
                                                                ⚠️ KYC
                                                            </Badge>
                                                        )}
                                                        {optimalQuote.kycRisk === 'high' && (
                                                            <Badge variant="outline" className="text-[10px] border-red-500/50 text-red-500" title={optimalQuote.kycMessage}>
                                                                ⚠️ KYC Required
                                                            </Badge>
                                                        )}
                                                        {optimalQuote.kycRisk === 'low' && optimalQuote.kycMessage && (
                                                            <Badge variant="outline" className="text-[10px] border-blue-500/50 text-blue-500" title={optimalQuote.kycMessage}>
                                                                ℹ️ Info
                                                            </Badge>
                                                        )}
                                                        <Badge variant="outline" className="border-yellow-500/50 text-yellow-500">
                                                            Best Rate
                                                        </Badge>
                                                    </div>
                                                </AlertDescription>
                                            </Alert>
                                            {allQuotes.length > 0 && (
                                                <div className="mt-3 border-t border-border pt-3">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="w-full justify-between text-sm font-medium"
                                                        onClick={() => setShowAllOptions(!showAllOptions)}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <List className="h-4 w-4" />
                                                            {showAllOptions ? 'Hide' : 'View'} All {allQuotes.length} Swap Options
                                                        </span>
                                                        {showAllOptions ? (
                                                            <ChevronUp className="h-4 w-4" />
                                                        ) : (
                                                            <ChevronDown className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                    {showAllOptions && (
                                                        <div className="mt-3 space-y-2 max-h-80 overflow-y-auto pr-2">
                                                            {/* Show available quotes first, sorted by best output */}
                                                            {allQuotes
                                                                .filter(q => q.available) // Show available quotes
                                                                .sort((a, b) => (b.estimatedOutput || 0) - (a.estimatedOutput || 0)) // Sort by best output
                                                                .map((quote, index) => {
                                                                    const isBest = quote.provider === optimalQuote?.provider;
                                                                    return (
                                                                        <div
                                                                            key={`${quote.provider}-${index}`}
                                                                            onClick={() => {
                                                                                if (!isBest) {
                                                                                    setOptimalQuote(quote);
                                                                                    toast.success(`Selected ${quote.provider}`, {
                                                                                        description: `You'll receive ${quote.estimatedOutput.toFixed(6)} ${toToken?.symbol}`,
                                                                                        duration: 3000,
                                                                                    });
                                                                                }
                                                                            }}
                                                                            className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                                                                                isBest
                                                                                    ? 'bg-yellow-500/10 border-yellow-500/50 shadow-sm'
                                                                                    : 'bg-card border-border hover:border-primary/30 hover:shadow-md'
                                                                            }`}
                                                                        >
                                                                            <div className="flex flex-col gap-1.5 flex-1">
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className={`font-semibold text-sm ${
                                                                                        isBest ? 'text-yellow-500' : 'text-foreground'
                                                                                }`}>
                                                                                    {quote.provider}
                                                                                </span>
                                                                                    {isBest && (
                                                                                        <Badge variant="outline" className="text-[10px] border-yellow-500/50 text-yellow-500 bg-yellow-500/10">
                                                                                            Best Rate
                                                                                    </Badge>
                                                                                )}
                                                                                    {quote.source === 'mm2' && (
                                                                                        <Badge variant="outline" className="text-[10px] border-primary/50 text-primary">
                                                                                            Atomic
                                                                                    </Badge>
                                                                                )}
                                                                                    {quote.kycRisk === 'medium' && (
                                                                                        <Badge variant="outline" className="text-[10px] border-orange-500/50 text-orange-500" title={quote.kycMessage}>
                                                                                            ⚠️ KYC
                                                                                        </Badge>
                                                                                    )}
                                                                                    {quote.kycRisk === 'high' && (
                                                                                        <Badge variant="outline" className="text-[10px] border-red-500/50 text-red-500" title={quote.kycMessage}>
                                                                                            ⚠️ KYC Required
                                                                                    </Badge>
                                                                                )}
                                                                            </div>
                                                                            {fromAmountNum && (
                                                                                    <div className="flex flex-col gap-0.5">
                                                                                        <span className="text-xs text-muted-foreground">
                                                                                            You receive: <span className="font-medium text-foreground">
                                                                                                {quote.estimatedOutput.toFixed(6)} {toToken?.symbol}
                                                                                            </span>
                                                                                        </span>
                                                                                        <span className="text-[10px] text-muted-foreground">
                                                                                            Fee: {(quote.fee * 100).toFixed(2)}%
                                                                                            {quote.providerFee !== undefined && quote.kawSwapFee !== undefined && quote.kawSwapFee > 0 && (
                                                                                                <span className="ml-1">
                                                                                                    ({quote.providerFee ? `${(quote.providerFee * 100).toFixed(2)}%` : '0%'} provider + {quote.kawSwapFee ? `${(quote.kawSwapFee * 100).toFixed(2)}%` : '0%'} platform)
                                                                                        </span>
                                                                                    )}
                                                                                        </span>
                                                                                        {quote.pathway && (
                                                                                            <span className="text-[10px] text-blue-500/80 mt-0.5">
                                                                                                Route: {quote.pathway}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex flex-col items-end gap-1">
                                                                                {isBest && (
                                                                                    <Badge className="bg-yellow-500 text-yellow-950 text-[10px]">
                                                                                        Selected
                                                                                </Badge>
                                                                                )}
                                                                                {quote.isCrossChain && (
                                                                                    <Badge variant="outline" className="text-[10px]">
                                                                                        Cross-Chain
                                                                                    </Badge>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            {allQuotes.filter(q => !q.available).length > 0 && (
                                                                <div className="mt-3 pt-3 border-t border-border">
                                                                    <p className="text-xs text-muted-foreground mb-2">
                                                                        Unavailable Options ({allQuotes.filter(q => !q.available).length}):
                                                                    </p>
                                                                    <div className="space-y-1">
                                                                        {allQuotes
                                                                            .filter(q => !q.available)
                                                                            .map((quote, index) => (
                                                                                <div key={`unavailable-${index}`} className="text-xs text-muted-foreground/70 p-2 rounded bg-muted/30">
                                                                                    <span className="font-medium">{quote.provider}</span>
                                                                                    {quote.error && (
                                                                                        <span className="ml-2">- {quote.error && typeof quote.error === 'string' ? quote.error.substring(0, 50) : 'Error'}</span>
                                                                                    )}
                                                                                    {quote.minAmount && (
                                                                                        <span className="ml-2">(Min: {quote.minAmount.toFixed(6)} {fromToken?.symbol})</span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    ) : (() => {
                                        // Check for minimum amount errors
                                        const minAmountErrors = allQuotes
                                            .filter(q => !q.available && q.minAmount !== undefined)
                                            .map(q => ({ provider: q.provider, minAmount: q.minAmount!, fromToken: fromToken?.symbol || '' }));
                                        
                                        const hasMinAmountError = minAmountErrors.length > 0 && fromToken && fromAmountNum;
                                        const minError = hasMinAmountError ? minAmountErrors[0] : null;
                                        
                                        return (
                                            <div className="space-y-3">
                                                <Alert variant="destructive">
                                                    <AlertTriangle className="h-4 w-4" />
                                                    <AlertDescription className="text-sm">
                                                        {hasMinAmountError && minError ? (
                                                            <div className="space-y-1">
                                                                <div className="font-semibold">Swap Amount Too Small</div>
                                                                <div>
                                                                    {minError.provider} requires a minimum of <strong>{minError.minAmount.toFixed(8)} {minError.fromToken}</strong>.
                                                                </div>
                                                                <div>
                                                                    Your amount: <strong>{fromAmountNum} {minError.fromToken}</strong>. Please increase the swap amount.
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            'No liquidity available. Please try again later.'
                                                        )}
                                                    </AlertDescription>
                                                </Alert>

                                                {/* Show which providers were tried and why (even if none were available) */}
                                                {allQuotes.length > 0 && (
                                                    <div className="rounded-lg border border-border bg-muted/20 p-3">
                                                        <div className="text-xs font-semibold mb-2">
                                                            Provider quote status
                                                        </div>
                                                        <div className="space-y-1">
                                                            {allQuotes.map((q, idx) => (
                                                                <div key={`${q.provider}-${idx}`} className="flex items-center justify-between text-xs">
                                                                    <span className="font-medium">{q.provider}</span>
                                                                    {q.available ? (
                                                                        <span className="text-green-600">
                                                                            OK • {q.estimatedOutput.toFixed(6)} {toToken?.symbol}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-muted-foreground">
                                                                            {q.error ? (typeof q.error === 'string' ? q.error : 'Unavailable') : 'Unavailable'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </>
                            )}

                            {/* MM2 Node Status - PAUSED (can be re-enabled in future) */}
                            {/* 
                            <div className="rounded-lg border border-border/70 bg-muted/20 p-4 space-y-2">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold">MM2 Node Status</p>
                                        <p className="text-xs text-muted-foreground">{mm2StatusDescription}</p>
                                    </div>
                                    <Badge
                                        variant={
                                            mm2Runtime.mode === 'embedded'
                                                ? 'default'
                                                : mm2Runtime.mode === 'custom'
                                                    ? 'secondary'
                                                    : 'outline'
                                        }
                                    >
                                        {mm2StatusLabel}
                                    </Badge>
                                </div>
                                {!mm2RouteReady && (
                                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                        <span>Unlock the embedded wallet or set a custom MM2 endpoint to enable atomic swaps.</span>
                                        <Button size="sm" variant="outline" onClick={handleOpenMM2Manager}>
                                            Open MM2 Wallet Manager
                                        </Button>
                                    </div>
                                )}
                            </div>
                            */}

                            {/* Atomic Swap Toggle */}
                            {atomicSwapAvailable && (
                                <div className="bg-accent/30 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Lock className="h-4 w-4 text-primary" />
                                            <div>
                                                <Label htmlFor="atomic-swap" className="text-sm font-medium">
                                                    Use Atomic Swap
                                                </Label>
                                                <p className="text-xs text-muted-foreground">
                                                    Trustless HTLC-based swap
                                                </p>
                                            </div>
                                        </div>
                                        <Switch
                                            id="atomic-swap"
                                            checked={useAtomicSwap}
                                            onCheckedChange={setUseAtomicSwap}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* From Token */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-sm text-muted-foreground">From</label>
                                    {isConnected && fromToken && (
                                        <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleFromAmountChange(fromTokenBalance.toString())}
                                            className="text-xs text-muted-foreground hover:text-primary transition-colors"
                                        >
                                                <div className="flex items-center gap-2">
                                                    <span>Balance: {fromTokenBalance.toFixed(4)} {fromToken.symbol}</span>
                                                    {isConnected && fromToken && isAtomicSwapCompatible(fromToken.symbol) && walletType === 'web3' && fromTokenBalance === 0 && (
                                                        <span className="text-xs text-amber-400 flex items-center gap-1" title="UTXO balances aren't visible via WalletConnect. Add your address manually to see balance.">
                                                            <Info className="h-3 w-3" />
                                                            Add Address
                                                        </span>
                                                    )}
                                                </div>
                                        </button>
                                            {walletType === 'web3' && isAtomicSwapCompatible(fromToken.symbol) && fromTokenBalance === 0 && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300"
                                                    onClick={() => setShowUTXOAddressesModal(true)}
                                                >
                                                    Add Address
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <Input
                                            ref={fromAmountInputRef}
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0.0"
                                            value={fromAmount}
                                            onFocus={() => {
                                                userIsTypingAmountRef.current = true;
                                            }}
                                            onBlur={() => {
                                                // Small delay before allowing clearing (cleanup previous timeout)
                                                if (fromAmountBlurTimeoutRef.current) {
                                                    clearTimeout(fromAmountBlurTimeoutRef.current);
                                                }
                                                fromAmountBlurTimeoutRef.current = setTimeout(() => {
                                                    userIsTypingAmountRef.current = false;
                                                    fromAmountBlurTimeoutRef.current = null;
                                                }, 500);
                                            }}
                                            onChange={(e) => handleFromAmountChange(e.target.value)}
                                            className="text-2xl h-14 bg-accent/50"
                                        />
                                    </div>
                                    <TokenSelector
                                        key={`from-${fromToken?.symbol || 'none'}`}
                                        tokens={tokens}
                                        selectedToken={fromToken}
                                        onSelectToken={(token) => {
                                            console.log('[SwapInterface] From token selected:', token);
                                            if (token) {
                                                // Force immediate state update
                                                setFromToken(token);
                                                console.log('[SwapInterface] setFromToken called with:', token);
                                            }
                                        }}
                                        showBalance={isConnected}
                                        balances={balances}
                                    />
                                </div>
                                {fromToken && fromAmount && parseFloat(fromAmount) > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        ≈ ${formatPrice(parseFloat(fromAmount) * (livePrices[fromToken.symbol] || 0))}
                                    </p>
                                )}
                            </div>

                            {/* Swap Button */}
                            <div className="flex justify-center -my-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="rounded-full h-10 w-10 border-2 bg-card hover:bg-accent z-10"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('[SwapInterface] Swap button clicked', { fromToken, toToken });
                                        handleSwapTokens();
                                    }}
                                    disabled={!fromToken || !toToken}
                                    title={!fromToken || !toToken ? "Select both tokens to swap" : "Swap tokens"}
                                >
                                    <ArrowDownUp className="h-5 w-5" />
                                </Button>
                            </div>

                            {/* To Token */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-sm text-muted-foreground">To</label>
                                    {isConnected && toToken && (
                                        <span className="text-xs text-muted-foreground">
                                            Balance: {toTokenBalance.toFixed(4)} {toToken.symbol}
                                        </span>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <Input
                                            ref={toAmountInputRef}
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0.0"
                                            value={toAmount}
                                            onFocus={() => {
                                                userIsTypingAmountRef.current = true;
                                            }}
                                            onBlur={() => {
                                                // Small delay before allowing clearing (cleanup previous timeout)
                                                if (toAmountBlurTimeoutRef.current) {
                                                    clearTimeout(toAmountBlurTimeoutRef.current);
                                                }
                                                toAmountBlurTimeoutRef.current = setTimeout(() => {
                                                    userIsTypingAmountRef.current = false;
                                                    toAmountBlurTimeoutRef.current = null;
                                                }, 500);
                                            }}
                                            onChange={(e) => handleToAmountChange(e.target.value)}
                                            className="text-2xl h-14 bg-accent/50"
                                        />
                                    </div>
                                    <TokenSelector
                                        key={`to-${toToken?.symbol || 'none'}`}
                                        tokens={tokens}
                                        selectedToken={toToken}
                                        onSelectToken={(token) => {
                                            console.log('[SwapInterface] To token selected:', token);
                                            if (token) {
                                                // Force immediate state update
                                                setToToken(token);
                                                console.log('[SwapInterface] setToToken called with:', token);
                                            }
                                        }}
                                        showBalance={isConnected}
                                        balances={balances}
                                        fromTokenSymbol={fromToken?.symbol}
                                        currentChainId={selectedChainId}
                                    />
                                </div>
                                {toToken && toAmount && parseFloat(toAmount) > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        ≈ ${formatPrice(parseFloat(toAmount) * (livePrices[toToken.symbol] || 0))}
                                    </p>
                                )}
                            </div>

                            {/* Destination Address Field (for cross-chain swaps) */}
                            {/* Show for cross-chain swaps even without wallet (aggregators need destination address) */}
                            {needsDestinationAddress && (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-medium">Destination Address</label>
                                        <span className="text-xs text-muted-foreground">{destinationAddressFormat}</span>
                                    </div>
                                    <Input
                                        ref={destinationAddressInputRef}
                                        type="text"
                                        placeholder="Enter destination address"
                                        value={destinationAddress}
                                        onFocus={() => {
                                            isUserTypingRef.current = true;
                                        }}
                                        onBlur={() => {
                                            // Small delay before allowing clearing, in case user clicks away temporarily
                                            setTimeout(() => {
                                                isUserTypingRef.current = false;
                                            }, 500);
                                        }}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Mark that user is typing and has entered a value
                                            isUserTypingRef.current = true;
                                            if (value.trim().length > 0) {
                                                userHasEnteredAddressRef.current = true;
                                            }
                                            setDestinationAddress(value);
                                            // Validate immediately on change
                                            if (value.trim()) {
                                                validateDestinationAddress(value);
                                            } else if (needsDestinationAddress) {
                                                setDestinationAddressError('Destination address is required for cross-chain swaps');
                                                // If user clears the field, reset the flag
                                                if (value.length === 0) {
                                                    userHasEnteredAddressRef.current = false;
                                                }
                                            } else {
                                                setDestinationAddressError('');
                                            }
                                            // Reset typing flag after a longer delay to prevent clearing while user pauses typing (cleanup previous timeout)
                                            if (destinationAddressTypingTimeoutRef.current) {
                                                clearTimeout(destinationAddressTypingTimeoutRef.current);
                                            }
                                            destinationAddressTypingTimeoutRef.current = setTimeout(() => {
                                                // Only reset if input is not focused
                                                if (document.activeElement !== destinationAddressInputRef.current) {
                                                    isUserTypingRef.current = false;
                                                }
                                                destinationAddressTypingTimeoutRef.current = null;
                                            }, 2000);
                                        }}
                                        className={`h-12 bg-accent/50 ${destinationAddressError ? 'border-destructive' : ''}`}
                                    />
                                    {destinationAddressError && (
                                        <p className="text-xs text-destructive">{destinationAddressError}</p>
                                    )}
                                    {destinationAddressReason && !destinationAddressError && (
                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Info className="h-3 w-3" />
                                            {destinationAddressReason}
                                        </p>
                                    )}
                                    
                                    {/* Warning: Some quotes may not appear without destination address */}
                                    {!destinationAddress && (
                                        <div className="flex items-start gap-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-md">
                                            <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                                            <p className="text-xs text-yellow-600 dark:text-yellow-500">
                                                <span className="font-medium">Note:</span> Some swap providers require a destination address to show quotes. Enter an address to see all available options.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Provider Name Display */}
                            {optimalQuote && (
                                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center gap-2">
                                    <Server className="h-4 w-4 text-primary" />
                                    <span className="text-sm font-medium text-primary">
                                        Routing via {optimalQuote.provider}
                                    </span>
                                </div>
                            )}

                            {/* Exchange Rate Info */}
                            {fromToken && toToken && fromAmount && parseFloat(fromAmount) > 0 && (
                                <div className="bg-accent/30 rounded-lg p-4 space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Exchange Rate</span>
                                        <span className="font-medium">
                                            1 {fromToken.symbol} = {formatExchangeRate(exchangeRate)} {toToken.symbol}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Price Impact</span>
                                        <span className={cn(
                                            "font-medium",
                                            priceImpact > 1 ? "text-orange-500" : priceImpact > 0.5 ? "text-yellow-500" : "text-green-500"
                                        )}>
                                            {priceImpact > 0 ? `${priceImpact.toFixed(2)}%` : '< 0.01%'}
                                        </span>
                                    </div>
                                    {priceImpact > 1 && (
                                        <div className="text-xs text-orange-500 flex items-center gap-1">
                                            <AlertTriangle className="h-3 w-3" />
                                            High price impact - consider splitting into smaller swaps
                                        </div>
                                    )}
                                    <div className="border-t border-border pt-2 space-y-1.5">
                                        {!useFallbackRouting && matchingOffer ? (
                                            <>
                                                {/* Only show DEX Fee for MM2 direct swaps, not aggregators */}
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-muted-foreground">DEX Fee ({dexFeePercent}%)</span>
                                                    <span className="font-medium">
                                                        {swapFees ? swapFees.dexFee.toFixed(8) : '0'} {fromToken.symbol}
                                                    </span>
                                                </div>
                                            </>
                                        ) : optimalQuote && (
                                            <>
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-muted-foreground">
                                                        {optimalQuote.provider} Fee ({(optimalQuote.providerFee !== undefined ? (optimalQuote.providerFee * 100).toFixed(2) : thirdPartyFeePercent)}%)
                                                    </span>
                                                    <span className="font-medium">
                                                        {optimalQuote.providerFee !== undefined 
                                                            ? (fromAmountNum * optimalQuote.providerFee).toFixed(8)
                                                            : thirdPartyFeeAmount.toFixed(8)
                                                        } {fromToken.symbol}
                                                    </span>
                                                </div>
                                                {optimalQuote.kawSwapFee !== undefined && optimalQuote.kawSwapFee > 0 && (
                                                    <div className="flex items-center justify-between text-sm">
                                                        <span className="text-muted-foreground">
                                                            KawSwap Fee ({(optimalQuote.kawSwapFee * 100).toFixed(2)}%)
                                                        </span>
                                                        <span className="font-medium">
                                                            {(fromAmountNum * optimalQuote.kawSwapFee).toFixed(8)} {fromToken.symbol}
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        <div className="flex items-center justify-between text-sm font-semibold pt-1 border-t border-border/50">
                                            <span className="text-muted-foreground">Total Fees</span>
                                            <span className="text-primary">
                                                {!useFallbackRouting 
                                                    ? (swapFees ? swapFees.totalFees.toFixed(8) : '0')
                                                    : optimalQuote && optimalQuote.providerFee !== undefined && optimalQuote.kawSwapFee !== undefined
                                                        ? ((fromAmountNum * optimalQuote.providerFee) + (optimalQuote.kawSwapFee > 0 ? fromAmountNum * optimalQuote.kawSwapFee : 0)).toFixed(8)
                                                        : thirdPartyFeeAmount.toFixed(8)
                                                } {fromToken.symbol}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm pt-1">
                                            <span className="text-muted-foreground">You Receive</span>
                                            <span className="font-medium">
                                                {optimalQuote && optimalQuote.available && optimalQuote.estimatedOutput 
                                                    ? optimalQuote.estimatedOutput.toFixed(8)
                                                    : toAmount || '0.00000000'} {toToken.symbol}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Slippage Tolerance</span>
                                        <span className="font-medium">{slippageTolerance}%</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Transaction Timeout</span>
                                        <span className="font-medium">{transactionTimeout} min</span>
                                    </div>
                                    {useAtomicSwap && (
                                        <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                                            <span className="text-muted-foreground">Swap Type</span>
                                            <span className="font-medium text-primary">Atomic (HTLC)</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Gas Estimate Display */}
                            {optimalQuote && optimalQuote.gasEstimate && (
                                <div className="bg-accent/30 rounded-lg p-4 space-y-2 border-t mt-2">
                                    <div className="text-sm font-semibold text-muted-foreground">Gas Estimate</div>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted-foreground">Source Chain Gas</span>
                                            <span className="font-medium">
                                                {optimalQuote.gasEstimate.sourceChain.estimatedCost.toFixed(6)} ETH
                                                {optimalQuote.gasEstimate.sourceChain.estimatedCostUSD && (
                                                    <span className="text-muted-foreground ml-1">
                                                        (${optimalQuote.gasEstimate.sourceChain.estimatedCostUSD.toFixed(2)})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        {optimalQuote.gasEstimate.destinationChain && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground">Relayer Fee</span>
                                                <span className="font-medium">
                                                    ${optimalQuote.gasEstimate.destinationChain.estimatedCostUSD?.toFixed(2) || 'N/A'}
                                                </span>
                                            </div>
                                        )}
                                        {optimalQuote.gasEstimate.totalGasCostUSD && (
                                            <div className="flex items-center justify-between pt-1 border-t">
                                                <span className="font-semibold">Total Gas Cost</span>
                                                <span className="font-semibold">
                                                    ${optimalQuote.gasEstimate.totalGasCostUSD.toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                        {optimalQuote.isCrossChain && (
                                            <div className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                                                <AlertTriangle className="h-3 w-3" />
                                                Cross-chain swap - may take longer to complete
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Gas Estimate Display */}
                            {optimalQuote && optimalQuote.gasEstimate && (
                                <div className="bg-accent/30 rounded-lg p-4 space-y-2 border-t mt-2">
                                    <div className="text-sm font-semibold text-muted-foreground">Gas Estimate</div>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted-foreground">Source Chain Gas</span>
                                            <span className="font-medium">
                                                {optimalQuote.gasEstimate.sourceChain.estimatedCost.toFixed(6)} ETH
                                                {optimalQuote.gasEstimate.sourceChain.estimatedCostUSD && (
                                                    <span className="text-muted-foreground ml-1">
                                                        (${optimalQuote.gasEstimate.sourceChain.estimatedCostUSD.toFixed(2)})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        {optimalQuote.gasEstimate.destinationChain && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground">Relayer Fee</span>
                                                <span className="font-medium">
                                                    ${optimalQuote.gasEstimate.destinationChain.estimatedCostUSD?.toFixed(2) || 'N/A'}
                                                </span>
                                            </div>
                                        )}
                                        {optimalQuote.gasEstimate.totalGasCostUSD && (
                                            <div className="flex items-center justify-between pt-1 border-t">
                                                <span className="font-semibold">Total Gas Cost</span>
                                                <span className="font-semibold">
                                                    ${optimalQuote.gasEstimate.totalGasCostUSD.toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                        {optimalQuote.isCrossChain && (
                                            <div className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                                                <AlertTriangle className="h-3 w-3" />
                                                Cross-chain swap - may take longer to complete
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Exchange Rate Info */}
                            {fromToken && toToken && fromAmount && parseFloat(fromAmount) > 0 && (
                                <div className="bg-accent/30 rounded-lg p-4 space-y-2">
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground pt-2 border-t border-border">
                                        <Info className="h-3 w-3" />
                                        <span>
                                            {useFallbackRouting && optimalQuote
                                                ? `Routed via ${optimalQuote.provider} for optimal pricing`
                                                : matchingOffer
                                                    ? 'Fees support DEX operations'
                                                    : ''
                                            }
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Action Button */}
                            <Button
                                className="w-full h-14 text-lg"
                                size="lg"
                                onClick={handleSwap}
                                disabled={(() => {
                                    // Basic validation
                                    if (!fromAmount || parseFloat(fromAmount) <= 0 || isSwapping) {
                                        return true;
                                    }
                                    
                                    // Destination address validation
                                    if (needsDestinationAddress && (!destinationAddress?.trim() || !!destinationAddressError)) {
                                        return true;
                                    }
                                    
                                    // If there's a quote that requires wallet signing, wallet must be connected
                                    if (optimalQuote && requiresWalletSigning(optimalQuote.provider) && !isConnected) {
                                        return true;
                                    }
                                    
                                    // If no quote, check if UTXO token can proceed (with or without wallet)
                                    if (!optimalQuote) {
                                        const isUTXOTokenForQuote = fromToken && isAtomicSwapCompatible(fromToken.symbol);
                                        
                                        if (isUTXOTokenForQuote) {
                                            // For UTXO tokens, allow swap even without wallet connected
                                            // Aggregators will provide deposit address for manual sending
                                            // Only require destination address if needed
                                            const canProceed = !needsDestinationAddress || (destinationAddress?.trim() && !destinationAddressError);
                                            
                                            // Debug logging
                                            console.log('[Button] No Quote - UTXO Token Check:', {
                                                fromToken: fromToken?.symbol,
                                                isUTXO: true,
                                                needsDest: needsDestinationAddress,
                                                hasDest: !!destinationAddress?.trim(),
                                                destError: !!destinationAddressError,
                                                canProceed,
                                                willDisable: !canProceed
                                            });
                                            
                                            // Disable only if destination address is required but missing/invalid
                                            return !canProceed;
                                        }
                                        
                                        // For non-UTXO tokens, disable if no quote
                                        return true;
                                    }
                                    
                                    // If we have a quote, button is enabled (unless wallet signing required and not connected - already checked above)
                                    return false;
                                })()}
                            >
                                {isSwapping ? (
                                    <>
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        Swapping...
                                    </>
                                ) : !isConnected && optimalQuote && requiresWalletSigning(optimalQuote.provider) ? (
                                    'Connect Wallet to Swap'
                                ) : !fromAmount || parseFloat(fromAmount) <= 0 ? (
                                    'Enter Amount'
                                ) : !optimalQuote && fromToken && isAtomicSwapCompatible(fromToken.symbol) ? (
                                    // UTXO token without quote - can proceed with deposit address
                                    (!needsDestinationAddress || (destinationAddress?.trim() && !destinationAddressError)) ? (
                                        (walletType === 'web3' && isConnected && walletAddress) ? 'Sign Transaction' : 'Get Deposit Address'
                                    ) : (
                                        'Enter Destination Address'
                                    )
                                ) : useFallbackRouting && !optimalQuote ? (
                                    'No Liquidity Available'
                                ) : needsDestinationAddress && (!destinationAddress || !!destinationAddressError) ? (
                                    'Enter Destination Address'
                                ) : fromToken && isAtomicSwapCompatible(fromToken.symbol) && optimalQuote && !requiresWalletSigning(optimalQuote.provider) ? (
                                    // UTXO token with quote - manual deposit address flow
                                    'Get Deposit Address'
                                ) : useAtomicSwap ? (
                                    <>
                                        <Lock className="mr-2 h-5 w-5" />
                                        Use Atomic Swap Interface
                                    </>
                                ) : (
                                    'Swap Now'
                                )}
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Transaction Modal */}
            <Dialog open={showTransactionModal} onOpenChange={setShowTransactionModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {transactionStatus === 'confirming' && 'Confirm Swap'}
                            {transactionStatus === 'processing' && 'Processing Transaction'}
                            {transactionStatus === 'success' && 'Swap Successful'}
                            {transactionStatus === 'error' && 'Transaction Failed'}
                        </DialogTitle>
                        <DialogDescription>
                            {transactionStatus === 'confirming' && 'Please review your transaction details'}
                            {transactionStatus === 'processing' && 'Your transaction is being processed on the blockchain'}
                            {transactionStatus === 'success' && 'Your swap has been completed successfully'}
                            {transactionStatus === 'error' && 'There was an error processing your transaction'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {transactionStatus === 'confirming' && (
                            <div className="space-y-3">
                                <div className="bg-accent/30 rounded-lg p-4 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">From</span>
                                        <div className="flex items-center gap-2">
                                            {fromToken && (
                                                <span className="font-medium">{fromAmount} {fromToken.symbol}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <ArrowDownUp className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">To</span>
                                        <div className="flex items-center gap-2">
                                            {toToken && (
                                                <span className="font-medium">{toAmount} {toToken.symbol}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="border-t border-border pt-2 mt-2 space-y-1">
                                        {!useFallbackRouting && matchingOffer ? (
                                            <>
                                                {/* Only show DEX Fee for MM2 direct swaps, not aggregators */}
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-muted-foreground">DEX Fee ({dexFeePercent}%)</span>
                                                    <span>{swapFees ? swapFees.dexFee.toFixed(8) : '0'} {fromToken?.symbol}</span>
                                                </div>
                                            </>
                                        ) : optimalQuote && (
                                            <>
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-muted-foreground">
                                                        {optimalQuote.provider} Fee ({(optimalQuote.providerFee !== undefined ? (optimalQuote.providerFee * 100).toFixed(2) : thirdPartyFeePercent)}%)
                                                    </span>
                                                    <span>
                                                        {optimalQuote.providerFee !== undefined 
                                                            ? (fromAmountNum * optimalQuote.providerFee).toFixed(8)
                                                            : thirdPartyFeeAmount.toFixed(8)
                                                        } {fromToken?.symbol}
                                                    </span>
                                                </div>
                                                {optimalQuote.kawSwapFee !== undefined && optimalQuote.kawSwapFee > 0 && (
                                                    <div className="flex items-center justify-between text-xs">
                                                        <span className="text-muted-foreground">
                                                            KawSwap Fee ({(optimalQuote.kawSwapFee * 100).toFixed(2)}%)
                                                        </span>
                                                        <span>{(fromAmountNum * optimalQuote.kawSwapFee).toFixed(8)} {fromToken?.symbol}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        <div className="flex items-center justify-between text-xs font-semibold pt-1 border-t border-border/50">
                                            <span className="text-muted-foreground">Total Fees</span>
                                            <span className="text-primary">
                                                {!useFallbackRouting && matchingOffer
                                                    ? (swapFees ? swapFees.totalFees.toFixed(8) : '0')
                                                    : optimalQuote && optimalQuote.providerFee !== undefined && optimalQuote.kawSwapFee !== undefined
                                                        ? ((fromAmountNum * optimalQuote.providerFee) + (optimalQuote.kawSwapFee > 0 ? fromAmountNum * optimalQuote.kawSwapFee : 0)).toFixed(8)
                                                        : thirdPartyFeeAmount.toFixed(8)
                                                } {fromToken?.symbol}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-xs text-muted-foreground text-center">
                                    Confirming transaction...
                                </div>
                            </div>
                        )}

                        {transactionStatus === 'processing' && (
                            <div className="flex flex-col items-center justify-center py-8 space-y-4">
                                <Loader2 className="h-16 w-16 animate-spin text-primary" />
                                <p className="text-sm text-muted-foreground">
                                    Processing your swap on the blockchain...
                                </p>
                            </div>
                        )}

                        {transactionStatus === 'success' && (
                            <div className="flex flex-col items-center justify-center py-8 space-y-4">
                                <CheckCircle2 className="h-16 w-16 text-green-500" />
                                <div className="text-center space-y-2">
                                    <p className="font-medium">
                                        Swapped {fromAmount} {fromToken?.symbol} for {toAmount} {toToken?.symbol}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Transaction completed successfully
                                    </p>
                                </div>
                            </div>
                        )}

                        {transactionStatus === 'error' && (
                            <div className="flex flex-col items-center justify-center py-8 space-y-4">
                                <XCircle className="h-16 w-16 text-destructive" />
                                <div className="text-center space-y-2">
                                    <p className="font-medium text-destructive">
                                        Transaction Failed
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {errorMessage || 'An error occurred while processing your transaction'}
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowTransactionModal(false);
                                        setTransactionStatus('idle');
                                    }}
                                >
                                    Close
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Custom Token Modal */}
            <AddCustomTokenModal 
                open={showAddTokenModal} 
                onOpenChange={setShowAddTokenModal}
            />
            {showCustomTokensManager && (
                <CustomTokensManager
                    open={showCustomTokensManager}
                    onOpenChange={setShowCustomTokensManager}
                />
            )}

            {/* Available Swaps Modal */}
            <AvailableSwapsModal
                open={showAvailableSwapsModal}
                onOpenChange={setShowAvailableSwapsModal}
                onSelectOffer={handleSelectOffer}
            />

            {/* Settings Modal */}
            <SettingsModal
                open={showSettingsModal}
                onOpenChange={setShowSettingsModal}
                slippageTolerance={slippageTolerance}
                onSlippageChange={setSlippageTolerance}
                transactionTimeout={transactionTimeout}
                onTimeoutChange={setTransactionTimeout}
            />

            {/* Add UTXO Addresses Modal */}
            <AddUTXOAddressesModal
                open={showUTXOAddressesModal}
                onOpenChange={setShowUTXOAddressesModal}
            />

            {/* Deposit Address Modal */}
            {depositInfo && (
                <DepositAddressModal
                    open={showDepositModal}
                    onOpenChange={setShowDepositModal}
                    depositAddress={depositInfo.depositAddress}
                    amount={depositInfo.amount}
                    token={depositInfo.token}
                    exchangeId={depositInfo.exchangeId}
                    provider={depositInfo.provider}
                    destinationAddress={depositInfo.destinationAddress}
                    toToken={depositInfo.toToken}
                    payinExtraId={depositInfo.payinExtraId}
                />
            )}
        </>
    );
}
